import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Avatar, Tag, Typography, Input, Select, DatePicker, Space, Row, Col, Card, Tooltip, Badge, message, Modal, Divider, Alert, Pagination, Timeline, Statistic, InputNumber, Form } from 'antd';
import SearchFieldsSelect, { useSearchFields, getSearchFieldsPlaceholder } from '../../Components/SearchFieldsSelect/SearchFieldsSelect';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  CarOutlined, 
  TagOutlined, 
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  SyncOutlined,
  StopOutlined,
  EditOutlined,
  SaveOutlined,
  PlusOutlined,
  DollarCircleOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  CopyOutlined,
  ClearOutlined,
  TeamOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ShopOutlined,
  CreditCardOutlined,
  CloseCircleOutlined,
  WalletOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  CommentOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import API from '../../API/API';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { YardModal } from '../LeadDetails/LeadDetails';
import YardReassignmentHistoryModal from '../../Components/YardReassignmentHistory/YardReassignmentHistoryModal';
import { getAssignmentOrPrimaryYardValue } from '../LeadDetails/utils/yardContactOptions';
import PurchaseOrderTemplate from '../LeadDetails/components/PurchaseOrderTemplate';
import { isOrderDecisionUnset, isPartStatusUnset } from '../../utils/orderStatus';

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const toPST = (date) => {
    if (!date) return null;
    return dayjs(date).tz('America/Los_Angeles');
};

const getCurrentYardAssignment = (yards = []) => {
    const assignments = Array.isArray(yards) ? yards.filter(Boolean) : [];

    for (let index = assignments.length - 1; index >= 0; index -= 1) {
        if (assignments[index]?.isCurrent === true) {
            return assignments[index];
        }
    }

    return null;
};

const YardWarrantyCell = ({ record, onRefresh }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editingValue, setEditingValue] = useState('');
    const [saving, setSaving] = useState(false);
    const currentYard = getCurrentYardAssignment(record.yard);
    const yardWarranty = currentYard?.yardWarranty?.trim();

    const handleEdit = () => {
        setEditingValue(yardWarranty || '');
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (!currentYard) {
            message.error('No current yard assignment found');
            return;
        }

        const updatedYards = record.yard
            .filter(Boolean)
            .map((yard) => (
                yard === currentYard
                    ? { ...yard, yardWarranty: editingValue.trim() }
                    : yard
            ));

        setSaving(true);
        try {
            const response = await API.UpdateLead(record._id, { yard: updatedYards });

            if (response?.status || response?.success) {
                message.success('Yard warranty updated successfully');
                setIsEditing(false);
                if (onRefresh) {
                    await onRefresh();
                }
            } else {
                message.error(response?.message || 'Failed to update yard warranty');
            }
        } catch (error) {
            console.error('Error updating yard warranty:', error);
            message.error('Failed to update yard warranty');
        } finally {
            setSaving(false);
        }
    };

    if (isEditing) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Input
                    value={editingValue}
                    onChange={(event) => setEditingValue(event.target.value)}
                    onPressEnter={handleSave}
                    placeholder="Enter warranty"
                    size="small"
                    autoFocus
                    disabled={saving}
                />
                <Space size={4} style={{ justifyContent: 'center' }}>
                    <Tooltip title="Save">
                        <Button
                            type="primary"
                            size="small"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            loading={saving}
                        />
                    </Tooltip>
                    <Tooltip title="Cancel">
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => setIsEditing(false)}
                            disabled={saving}
                        />
                    </Tooltip>
                </Space>
            </div>
        );
    }

    return (
        <div
            style={{
                minHeight: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                padding: '6px 4px',
                borderRadius: 8,
                background: yardWarranty ? '#f6ffed' : '#fafafa'
            }}
        >
            <Tooltip title={yardWarranty || 'No warranty saved for the current yard'}>
                <Tag
                    color={yardWarranty ? 'green' : 'default'}
                    style={{
                        maxWidth: currentYard ? 'calc(100% - 28px)' : '100%',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 11
                    }}
                >
                    {yardWarranty || 'N/A'}
                </Tag>
            </Tooltip>
            {currentYard && (
                <Tooltip title="Edit Yard Warranty">
                    <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={handleEdit}
                        style={{ color: '#1890ff', minWidth: 24, padding: 0 }}
                    />
                </Tooltip>
            )}
        </div>
    );
};

// Create this component OUTSIDE your OrderProcess component
const PaymentIdCell = ({ record, isAdmin, onRefresh }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editingValue, setEditingValue] = useState('');
    const [saving, setSaving] = useState(false);
    
    const advancePayments = record.advancePayments || [];
    const latestPayment = advancePayments.length > 0 
        ? advancePayments.sort((a, b) => new Date(b.date) - new Date(a.date))[0]
        : null;
    
    const transactionId = latestPayment?.transactionId;
    const paymentMethod = latestPayment?.paymentMethod;
    const paymentId = latestPayment?._id || latestPayment?.id;
    
    const handleEdit = () => {
        setIsEditing(true);
        setEditingValue(transactionId || '');
    };
    
    const handleSave = async () => {
        if (!paymentId) {
            message.error('No payment found');
            return;
        }
        
        setSaving(true);
        try {
            const paymentIndex = advancePayments.findIndex(p => (p._id || p.id) === paymentId);
            if (paymentIndex === -1) throw new Error('Payment not found');
            
            const updatedPayments = [...advancePayments];
            updatedPayments[paymentIndex] = {
                ...updatedPayments[paymentIndex],
                transactionId: editingValue.trim() || undefined
            };
            
            const response = await API.UpdateLead(record._id, { advancePayments: updatedPayments });
            
            if (response?.status) {
                message.success('Payment ID updated!');
                setIsEditing(false);
                if (onRefresh) onRefresh();
            } else {
                message.error('Update failed');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to update');
        } finally {
            setSaving(false);
        }
    };
    
    if (!latestPayment) {
        return <Tag color="default">No Payment</Tag>;
    }
    
    if (isEditing) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Input
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    placeholder="Enter Payment ID"
                    size="small"
                    autoFocus
                />
                <Space size={4}>
                    <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} />
                    <Button size="small" icon={<CloseOutlined />} onClick={() => setIsEditing(false)} />
                </Space>
            </div>
        );
    }
    
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {transactionId ? (
                    <Tooltip title={`Payment Method: ${paymentMethod || 'N/A'}`}>
                        <Tag 
                            color="blue" 
                            style={{ borderRadius: 12, padding: '4px 10px', fontSize: 11, cursor: 'pointer', flex: 1, textAlign: 'center' }}
                            onClick={() => { navigator.clipboard.writeText(transactionId); message.success('Copied!'); }}
                        >
                            <CreditCardOutlined style={{ marginRight: 4 }} />
                            {transactionId.length > 20 ? `${transactionId.substring(0, 15)}...` : transactionId}
                        </Tag>
                    </Tooltip>
                ) : (
                    <Tag color="warning" style={{ borderRadius: 12, padding: '4px 10px', fontSize: 11, flex: 1, textAlign: 'center' }}>
                        No Payment ID
                    </Tag>
                )}
                {isAdmin && (
                    <Tooltip title="Edit Payment ID">
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={handleEdit} style={{ color: '#1890ff' }} />
                    </Tooltip>
                )}
            </div>
            {paymentMethod && transactionId && (
                <Text type="secondary" style={{ fontSize: 10, textAlign: 'center' }}>via {paymentMethod}</Text>
            )}
        </div>
    );
};
const CompanyCardCell = ({ record, isAdmin, onRefresh }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editingCardNumber, setEditingCardNumber] = useState('');
    const [editingBankName, setEditingBankName] = useState('');
    const [editingUpdatedAt, setEditingUpdatedAt] = useState(null);
    const [saving, setSaving] = useState(false);
    const [cardError, setCardError] = useState('');
    
    const companyCard = record.companyCardDetails || {};
    const cardNumber = companyCard.cardNumber || "No Card";
    const bankName = companyCard.bankName || "No Bank";
    const updatedAt = companyCard.updatedAt;
    
    // US Card Validation Function
    const validateCardNumber = (cardNumber) => {
        if (!cardNumber || cardNumber.trim() === '') return true; // Allow empty
        
        // Remove spaces and dashes
        const cleanNumber = cardNumber.replace(/[\s-]/g, '');
        
        // Check if it contains only digits
        if (!/^\d+$/.test(cleanNumber)) {
            return { valid: false, message: 'Card number must contain only digits' };
        }
        
        // Check length (13-19 digits for most cards)
        if (cleanNumber.length < 13 || cleanNumber.length > 19) {
            return { valid: false, message: 'Card number must be between 13-19 digits' };
        }
        
        // Luhn Algorithm (Mod 10 check)
        let sum = 0;
        let isEven = false;
        
        for (let i = cleanNumber.length - 1; i >= 0; i--) {
            let digit = parseInt(cleanNumber.charAt(i), 10);
            
            if (isEven) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }
            sum += digit;
            isEven = !isEven;
        }
        
        const isValid = (sum % 10) === 0;
        
        if (!isValid) {
            return { valid: false, message: 'Invalid card number (failed validation check)' };
        }
        
        // Detect card type (optional)
        let cardType = 'Unknown';
        if (/^4/.test(cleanNumber)) cardType = 'Visa';
        else if (/^5[1-5]/.test(cleanNumber)) cardType = 'MasterCard';
        else if (/^3[47]/.test(cleanNumber)) cardType = 'American Express';
        else if (/^6(?:011|5)/.test(cleanNumber)) cardType = 'Discover';
        else if (/^35(2[89]|[3-8])/.test(cleanNumber)) cardType = 'JCB';
        else if (/^30[0-5]/.test(cleanNumber)) cardType = 'Diners Club';
        
        return { valid: true, message: '', cardType };
    };
    
    const handleCardNumberChange = (value) => {
        setEditingCardNumber(value);
        setCardError('');
        
        if (value && value.trim() !== '') {
            const validation = validateCardNumber(value);
            if (!validation.valid) {
                setCardError(validation.message);
            }
        }
    };
    
    const handleEdit = () => {
        setIsEditing(true);
        setEditingCardNumber(cardNumber === "No Card" ? '' : cardNumber);
        setEditingBankName(bankName === "No Bank" ? '' : bankName);
        setEditingUpdatedAt(updatedAt ? dayjs(updatedAt) : dayjs());
        setCardError('');
    };
    
    const handleSave = async () => {
        // Validate card number if provided
        if (editingCardNumber && editingCardNumber.trim() !== '') {
            const validation = validateCardNumber(editingCardNumber);
            if (!validation.valid) {
                message.error(validation.message);
                return;
            }
        }
        
        // Validate date is selected
        if (!editingUpdatedAt) {
            message.warning('Please select a date');
            return;
        }
        
        setSaving(true);
        try {
            const payload = {
                companyCardDetails: {
                    cardNumber: editingCardNumber.trim() || "No Card",
                    bankName: editingBankName.trim() || "No Bank",
                    updatedAt: editingUpdatedAt.toISOString()
                }
            };
            
            const response = await API.UpdateLead(record._id, payload);
            
            if (response?.status) {
                message.success('Company card details updated!');
                setIsEditing(false);
                if (onRefresh) onRefresh();
            } else {
                message.error('Update failed');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to update');
        } finally {
            setSaving(false);
        }
    };
    
  
    
    if (isEditing) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>
                    <Input
                        value={editingCardNumber}
                        onChange={(e) => handleCardNumberChange(e.target.value)}
                        placeholder="Card Number (e.g., 4111111111111111)"
                        size="small"
                        style={{ borderRadius: 6, borderColor: cardError ? '#ff4d4f' : undefined }}
                        status={cardError ? 'error' : ''}
                        maxLength={19}
                    />
                    {cardError && (
                        <Text type="danger" style={{ fontSize: 10, marginTop: 2, display: 'block' }}>
                            {cardError}
                        </Text>
                    )}
                </div>
                <Input
                    value={editingBankName}
                    onChange={(e) => setEditingBankName(e.target.value)}
                    placeholder="Bank Name (e.g., Chase, Bank of America)"
                    size="small"
                    style={{ borderRadius: 6 }}
                />
                <DatePicker
                    value={editingUpdatedAt}
                    onChange={(date) => setEditingUpdatedAt(date)}
                    format="MM/DD/YYYY"
                    size="small"
                    placeholder="Select date"
                    style={{ width: '100%', borderRadius: 6 }}
                />
                <Space size={4}>
                    <Button 
                        size="small" 
                        type="primary" 
                        icon={<SaveOutlined />} 
                        onClick={handleSave} 
                        loading={saving}
                        style={{ borderRadius: 6 }}
                        disabled={!!cardError}
                    />
                    <Button 
                        size="small" 
                        icon={<CloseOutlined />} 
                        onClick={() => setIsEditing(false)}
                        style={{ borderRadius: 6 }}
                    />
                </Space>
            </div>
        );
    }
    
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Tooltip title={cardNumber !== "No Card" ? `Full card: ${cardNumber}` : cardNumber}>
                            <Tag 
                                color={cardNumber !== "No Card" ? 'blue' : 'default'}
                                style={{ 
                                    borderRadius: 12, 
                                    padding: '2px 8px',
                                    fontSize: 11,
                                    cursor: cardNumber !== "No Card" ? 'pointer' : 'default',
                                    width: 'fit-content'
                                }}
                                onClick={() => {
                                    if (cardNumber !== "No Card") {
                                        navigator.clipboard.writeText(cardNumber);
                                        message.success('Card number copied!');
                                    }
                                }}
                            >
                                <CreditCardOutlined style={{ marginRight: 4 }} />
                                {cardNumber !== "No Card" ? cardNumber : cardNumber}
                            </Tag>
                        </Tooltip>
                        <Tag 
                            color={bankName !== "No Bank" ? 'green' : 'default'}
                            style={{ 
                                borderRadius: 12, 
                                padding: '2px 8px',
                                fontSize: 11,
                                width: 'fit-content'
                            }}
                        >
                            {bankName}
                        </Tag>
                    </div>
                </div>
                {isAdmin && (
                    <Tooltip title="Edit Company Card Details">
                        <Button 
                            type="text" 
                            size="small" 
                            icon={<EditOutlined />} 
                            onClick={handleEdit}
                            style={{ color: '#1890ff' }}
                        />
                    </Tooltip>
                )}
            </div>
            {updatedAt && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CalendarOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                    <Text type="secondary" style={{ fontSize: 10 }}>
                        Last updated: {dayjs(updatedAt).format('MM/DD/YYYY')}
                    </Text>
                </div>
            )}
        </div>
    );
};
const OrderProcess = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [yardDetailsMap, setYardDetailsMap] = useState({});

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  
  const maxTotalRef = useRef(0);

  // Agent filter state
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [loginAgent, setLoginAgent] = useState({});

  // Editing states
  const [editingId, setEditingId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [selectedYard, setSelectedYard] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [yardModalVisible, setYardModalVisible] = useState(false);
const [selectedLeadForYard, setSelectedLeadForYard] = useState(null);
const [yardHistoryLead, setYardHistoryLead] = useState(null);
const [poModalVisible, setPoModalVisible] = useState(false);
const [selectedLeadForPO, setSelectedLeadForPO] = useState(null);
  const [serviceAgentsMap, setServiceAgentsMap] = useState({});
  const [loadingServiceAgents, setLoadingServiceAgents] = useState(false);
  const [selectedServiceAgentId, setSelectedServiceAgentId] = useState(null);
   const [editingRelocatedDate, setEditingRelocatedDate] = useState(null);
   const [statusHistoryModalVisible, setStatusHistoryModalVisible] = useState(false);
   const [statusHistoryModalTitle, setStatusHistoryModalTitle] = useState('');
   const [statusHistoryModalEntries, setStatusHistoryModalEntries] = useState([]);
   // Add these with your other state declarations (around line 80-100)
const [isMilesPromiseModalVisible, setIsMilesPromiseModalVisible] = useState(false);
const [selectedLeadForMilesPromise, setSelectedLeadForMilesPromise] = useState(null);
const [newMilesPromiseComment, setNewMilesPromiseComment] = useState('');
const [addingMilesPromiseComment, setAddingMilesPromiseComment] = useState(false);

  // Modal state for Amount edit
  const [isAmountModalVisible, setIsAmountModalVisible] = useState(false);
  const [selectedLeadForAmount, setSelectedLeadForAmount] = useState(null);
  const [savingAmount, setSavingAmount] = useState(false);
  const [amountForm] = Form.useForm();
const [trackingModalVisible, setTrackingModalVisible] = useState(false);
const [selectedTrackingRecord, setSelectedTrackingRecord] = useState(null);
const [trackingForm] = Form.useForm();
const [updatingTracking, setUpdatingTracking] = useState(false);
// Add these with your other state declarations
const [serviceAgentModalVisible, setServiceAgentModalVisible] = useState(false);
const [selectedLeadForServiceAgent, setSelectedLeadForServiceAgent] = useState(null);
const [selectedServiceAgentForLead, setSelectedServiceAgentForLead] = useState(null);
const [savingServiceAgent, setSavingServiceAgent] = useState(false);
const [editingSpecification, setEditingSpecification] = useState(null);
const [editingSpecValue, setEditingSpecValue] = useState('');
const [updatingSpecification, setUpdatingSpecification] = useState(false);
 

  const [sortedInfo, setSortedInfo] = useState({
    field: 'orderDate',
    order: 'descend',
  });
  const [filteredInfo, setFilteredInfo] = useState({
    part: null,
    zohoStatus: null,
    invoiceStatus: null,
    purchaseOrderStatus: null,
    agent: null,
  });
  
  // Search and filter states
  const [searchText, setSearchText] = useState('');
  // The search a request uses: the box's text, set 0.5 s after typing pauses
  // (at once when the box is cleared), so a search is not sent per keystroke.
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const trimmed = searchText.trim();
    if (!trimmed) {
      setSearchQuery('');
      return undefined;
    }
    const timer = setTimeout(() => setSearchQuery(trimmed), 500);
    return () => clearTimeout(timer);
  }, [searchText]);
  const [searchFields, setSearchFields] = useSearchFields('order-process:search-fields');
  // The "Search in" group only changes the results of a search, so changing it
  // with an empty search box sends no request.
  const effectiveSearchFields = searchQuery ? searchFields : null;
const [dateRange, setDateRange] = useState(() => {
    const now = dayjs();
    return [now.startOf('month'), now.endOf('month')];
});
  const [quickDateFilter, setQuickDateFilter] = useState('thisMonth');
  const [editingInvoiceDate, setEditingInvoiceDate] = useState(null);

  // Statistics
  const [statistics, setStatistics] = useState({
    totalPending: 0,
    totalAmount: 0,
    averageAmount: 0,
    completedCount: 0,
    pendingCount: 0
  });

  const isAdmin = loginAgent?.role === "ADMIN";
  // Admins and service agents can cancel an order from the Part Status column
  const canCancelOrder = ['ADMIN', 'SERVICE-AGENT'].includes(loginAgent?.role);
   const [commentsModalVisible, setCommentsModalVisible] = useState(false);
    const [selectedLeadForComment, setSelectedLeadForComment] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [addingComment, setAddingComment] = useState(false);

  // Status options based on schema
  const zohoStatusOptions = [
    'SENT',
    'NOT-SENT',
    'RECEIVED',
    'DL-COPY',
    'EMAIL-APPROVAL',
    'COMPLETED', 
    'INCOMPLETE',
    'FAILED',
  ];

  const invoiceStatusOptions = [
    'INVOICE-SENT',
    'INVOICE-NOT-SENT'
  ];

  const purchaseOrderStatusOptions = [
    'PO-SENT',
    'PO-NOT-SENT',
    'PO-CONFIRMED',
    'PO-NOT-CONFIRMED'
  ];

  // Get user data on mount
  useEffect(() => {
    const UD = localStorage.getItem('userData');
    if (!UD) {
      navigate(ROUTES?.LOGIN);
      return;
    }
    const parsedUser = JSON.parse(UD);
    setLoginAgent(parsedUser);
    
    if (parsedUser?.role === "ADMIN") {
      fetchAgents();
    }
      fetchServiceAgents();
  }, [navigate]);
const handleUpdateSpecification = async (leadId, newSpecification) => {
    if (!leadId) return;
    
    const trimmedValue = newSpecification?.trim() || '';
    
    if (trimmedValue.length > 2000) {
        message.warning('Specification cannot exceed 2000 characters');
        return;
    }
    
    setUpdatingSpecification(true);
    try {
        // First, get the current lead data to preserve other vehicleInfo fields
        const currentLead = leads.find(lead => lead._id === leadId);
        const currentVehicleInfo = currentLead?.vehicleInfo || {};
        
        // Merge the specification with existing vehicleInfo
        const payload = {
            vehicleInfo: {
                ...currentVehicleInfo, // Preserve all existing fields
                specification: trimmedValue // Update only specification
            }
        };
        
        console.log('Sending payload with preserved data:', payload);
        
        const response = await API.UpdateLead(leadId, payload);
        
        if (response?.status) {
            message.success('Specification updated successfully!');
            fetchLeads(); // Refresh the leads list
        } else {
            message.error(response?.message || 'Failed to update specification');
        }
    } catch (error) {
        console.error('Error updating specification:', error);
        message.error('Failed to update specification');
    } finally {
        setUpdatingSpecification(false);
        setEditingSpecification(null);
        setEditingSpecValue('');
    }
};
const handleAddMilesPromiseComment = async () => {
    if (!newMilesPromiseComment.trim()) {
        message.warning('Please enter a comment');
        return;
    }
    
    if (!selectedLeadForMilesPromise) return;
    
    setAddingMilesPromiseComment(true);
    try {
        const existingComments = selectedLeadForMilesPromise.milesPromiseCustomer || [];
        const payload = {
            milesPromiseCustomer: [
                ...existingComments,
                {
                    comment: newMilesPromiseComment.trim(),
                    changedAt: new Date().toISOString()
                }
            ]
        };
        
        const response = await API.UpdateLead(selectedLeadForMilesPromise._id, payload);
        
        if (response?.status) {
            message.success('Miles Promise comment added successfully!');
            setNewMilesPromiseComment('');
            setIsMilesPromiseModalVisible(false);
            setSelectedLeadForMilesPromise(null);
            fetchLeads(); // Refresh the leads list
        } else {
            message.error(response?.message || 'Failed to add comment');
        }
    } catch (error) {
        console.error('Error adding miles promise comment:', error);
        message.error('Failed to add comment');
    } finally {
        setAddingMilesPromiseComment(false);
    }
};
  const handleReAssignYard = (record) => {
  setSelectedLeadForYard(record);
  setYardModalVisible(true);
};

const handleViewYardDetails = (record) => {
  navigate(`/lead-details/${record._id}`, {
    state: {
      sourceTab: 'order-process',
      activeTab: 'yard'
    }
  });
};

const handleYardModalClose = () => {
  setYardModalVisible(false);
  setSelectedLeadForYard(null);
};

const handleYardModalSuccess = () => {
  setYardModalVisible(false);
  setSelectedLeadForYard(null);
  fetchLeads(true);
  message.success('Yard reassigned successfully');
};

const handleOpenPOModal = (record) => {
  if (!hasYardAssigned(record)) {
    showMissingYardError();
    return;
  }
  if (!hasOrderServiceAgentAssigned(record)) {
    showMissingOrderServiceAgentError();
    return;
  }
  setSelectedLeadForPO(record);
  setPoModalVisible(true);
};

const handlePOModalClose = () => {
  setPoModalVisible(false);
  setSelectedLeadForPO(null);
};

const handlePOSendSuccess = () => {
  fetchLeads(true);
};

// Handle opening the service agent selection modal
const handleServiceAgentEdit = (record) => {
    const currentServiceAgentId = record.orderServiceHandledBy?._id || record.orderServiceHandledBy;
    setSelectedLeadForServiceAgent(record);
    setSelectedServiceAgentForLead(currentServiceAgentId || null);
    setServiceAgentModalVisible(true);
};

// Handle saving the service agent
const handleServiceAgentSave = async () => {
    if (!selectedLeadForServiceAgent) return;
    
    setSavingServiceAgent(true);
    
    try {
        const payload = {
            orderServiceHandledBy: selectedServiceAgentForLead || null // Set to null if unassigned
        };
        
        const response = await API.UpdateLead(selectedLeadForServiceAgent._id, payload);
        
        if (response?.status || response?.success) {
            message.success(selectedServiceAgentForLead ? 'Service agent assigned successfully!' : 'Service agent unassigned successfully!');
            
            // Update local state
            setLeads(prevLeads => 
                prevLeads.map(lead => 
                    lead._id === selectedLeadForServiceAgent._id ? { 
                        ...lead, 
                        orderServiceHandledBy: selectedServiceAgentForLead
                    } : lead
                )
            );
            
            // Close modal
            setServiceAgentModalVisible(false);
            setSelectedLeadForServiceAgent(null);
            setSelectedServiceAgentForLead(null);
            
            // Refresh leads to get populated agent data
            fetchLeads(true);
            fetchServiceAgents(); // Refresh the agents map
        } else {
            message.error(response?.message || 'Failed to assign service agent');
        }
    } catch (error) {
        console.error('Error assigning service agent:', error);
        message.error('Failed to save service agent assignment');
    } finally {
        setSavingServiceAgent(false);
    }
};
  const handleAddComment = async () => {
    if (!newComment.trim()) {
        message.warning('Please enter a comment');
        return;
    }

    if (!selectedLeadForComment) return;

    setAddingComment(true);
    try {
        const existingComments = selectedLeadForComment.paymentPageComment || [];
        const payload = {
            paymentPageComment: [
                ...existingComments,
                {
                    comment: newComment.trim(),
                    changedAt: new Date().toISOString()
                }
            ]
        };

        const response = await API.UpdateLead(selectedLeadForComment._id, payload);

        if (response?.status) {
            message.success('Comment added successfully!');
            setNewComment('');
            setCommentsModalVisible(false);
            setSelectedLeadForComment(null);
            // Refresh the leads list
            fetchLeads();
        } else {
            message.error(response?.message || 'Failed to add comment');
        }
    } catch (error) {
        console.error('Error adding comment:', error);
        message.error('Failed to add comment');
    } finally {
        setAddingComment(false);
    }
};

const formatCommentDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    });
};


const getServiceAgentName = (record) => {
    const serviceAgentId = record.orderServiceHandledBy?._id || record.orderServiceHandledBy;
    if (!serviceAgentId) return null;
    
    // Check if already populated as object
    if (record.orderServiceHandledBy && typeof record.orderServiceHandledBy === 'object') {
        return `${record.orderServiceHandledBy.firstName || ''} ${record.orderServiceHandledBy.lastName || ''}`.trim() || 
               record.orderServiceHandledBy.userName || 'Service Agent';
    }
    
    // Check in our map
    const agentInfo = serviceAgentsMap[serviceAgentId];
    return agentInfo?.name || null;
};

const getOrderServiceAgentId = (record) => {
    const serviceAgent = record?.orderServiceHandledBy;
    if (!serviceAgent) return null;
    if (typeof serviceAgent === 'object') {
        return serviceAgent._id || serviceAgent.id || serviceAgent.$oid || null;
    }
    return serviceAgent;
};

const hasOrderServiceAgentAssigned = (record) => Boolean(getOrderServiceAgentId(record));

const showMissingOrderServiceAgentError = () => {
    message.error('Please assign the Order Service-Agent');
};

const requiresOrderServiceAgentForStatus = (field) => field === 'purchaseOrderStatus';

const hasYardAssigned = (record) => Array.isArray(record?.yard) && record.yard.length > 0;

const showMissingYardError = () => {
    message.error('Please assign a yard first before updating document status');
};

const requiresYardForStatus = (field) => ['zohoStatus', 'invoiceStatus', 'purchaseOrderStatus'].includes(field);


  const fetchAgents = async () => {
    try {
      const response = await API.userGetAll();
      if (response?.success) {
        const salesAgents = (response.data || []).filter(user => user.role === "SALES-AGENT");
        setAgents(salesAgents);
      }
    } catch (error) {
      console.error("Failed to fetch agents", error);
    }
  };
const fetchServiceAgents = async () => {
    setLoadingServiceAgents(true);
    try {
        const response = await API.userGetAll();
        if (response?.success) {
            const agentsMap = {};
            // Get all users with SERVICE--AGENT role
            const serviceAgentsList = (response.data || []).filter(
                user => user.role === "SERVICE-AGENT" 
            );
            serviceAgentsList.forEach(agent => {
                agentsMap[agent._id] = {
                    name: `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.userName || agent.email,
                    email: agent.email,
                    phone: agent.directNumber,
                    role: agent.role
                };
            });
            setServiceAgentsMap(agentsMap);
        }
    } catch (error) {
        console.error("Failed to fetch service agents", error);
    } finally {
        setLoadingServiceAgents(false);
    }
};

  // Get agent name function
  const getAgentName = (record) => {
    if (record.assignedAgent?.name) return record.assignedAgent.name;
    if (record.agentName) return record.agentName;
    if (record.handledBy?.firstName && record.handledBy?.lastName) {
      return `${record.handledBy.firstName} ${record.handledBy.lastName}`;
    }
    if (record.handledBy?.name) return record.handledBy.name;
    return 'Unassigned';
  };

  // Get unique agents for filter
  const getUniqueAgents = () => {
    const agentNames = leads
      .map(lead => getAgentName(lead))
      .filter((value, index, self) => value && self.indexOf(value) === index);
    return agentNames;
  };

  // Get unique values for filters
  const getUniqueValues = (field) => {
    const values = leads
      .map(lead => {
        if (field === 'part') return lead.vehicleInfo?.part;
        if (field === 'zohoStatus') return lead.zohoStatus;
        if (field === 'invoiceStatus') return lead.invoiceStatus;
        if (field === 'purchaseOrderStatus') return lead.purchaseOrderStatus;
        return lead[field];
      })
      .filter((value, index, self) => value && self.indexOf(value) === index);
    return values;
  };

  // Handle Amount modal open
  const handleAmountEditOpen = (record) => {
    setSelectedLeadForAmount(record);
    amountForm.setFieldsValue({
      totalAmount: record.totalAmount || 0
    });
    setIsAmountModalVisible(true);
  };

  // Handle Amount modal cancel
  const handleAmountModalCancel = () => {
    setIsAmountModalVisible(false);
    setSelectedLeadForAmount(null);
    amountForm.resetFields();
  };

  // Handle Amount modal save
  const handleAmountModalSave = async () => {
    try {
      const values = await amountForm.validateFields();
      
      if (!values.totalAmount || values.totalAmount <= 0) {
        message.warning('Please enter a valid amount');
        return;
      }

      setSavingAmount(true);
      
      const currentLead = selectedLeadForAmount;
      const currentPaidAmount = currentLead?.paidAmount || 0;
      
      // Calculate new balance
      const newBalance = values.totalAmount - currentPaidAmount;
      
      const payload = {
        totalAmount: values.totalAmount,
        balanceDue: newBalance >= 0 ? newBalance : 0,
        payementAllow: 'No'
      };

      const response = await API.UpdateLead(currentLead._id, payload);

      if (response?.status) {
        message.success('Total amount updated successfully!');
        
        // Update local state
        setLeads(prevLeads => 
          prevLeads.map(lead => 
            lead._id === currentLead._id ? { 
              ...lead, 
              totalAmount: values.totalAmount,
              balanceDue: newBalance >= 0 ? newBalance : 0
            } : lead
          )
        );
        
        // Recalculate statistics
        const updatedLeads = leads.map(lead => 
          lead._id === currentLead._id ? { 
            ...lead, 
            totalAmount: values.totalAmount,
            balanceDue: newBalance >= 0 ? newBalance : 0
          } : lead
        );
        calculateStatistics(updatedLeads);
        
        // Close modal
        handleAmountModalCancel();
      } else {
        message.error(response?.message || 'Failed to update total amount');
      }
    } catch (error) {
      console.error('Error updating total amount:', error);
      message.error('Failed to save total amount');
    } finally {
      setSavingAmount(false);
    }
  };

  // Expanded row render function - shows yard details
  const expandedRowRender = (record) => {
    const yards = record.yard || [];
    
    if (yards.length === 0) {
      return (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Text type="secondary">No yard information available</Text>
        </div>
      );
    }

    return (
      <div style={{ 
        padding: '20px 24px', 
        background: '#fafafa',
        borderRadius: 12,
        margin: '8px 0'
      }}>
        <Title level={5} style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShopOutlined style={{ color: '#722ed1' }} />
          Yard Details History
        </Title>
        
        <Timeline mode="left">
          {yards.map((yard, index) => {
            const yardDetails = yardDetailsMap[yard.yardId] || {};
            const isLatest = index === yards.length - 1;
            
            return (
              <Timeline.Item
                key={index}
                color={isLatest ? 'green' : 'blue'}
                dot={isLatest ? <CheckCircleOutlined /> : <ShopOutlined />}
              >
                <Card 
                  size="small" 
                  style={{ 
                    marginBottom: 16,
                    borderRadius: 12,
                    border: isLatest ? '1px solid #52c41a' : '1px solid #e8e8e8',
                    background: isLatest ? '#f6ffed' : '#ffffff'
                  }}
                >
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                          <Tag color={isLatest ? 'green' : 'blue'}>
                            {isLatest ? 'Current Yard' : `Version ${index + 1}`}
                          </Tag>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {yard.yardConfirmDate ? dayjs(yard.yardConfirmDate).format('MMM DD, YYYY hh:mm A') : 'Date not available'}
                          </Text>
                        </Space>
                      </div>
                    </Col>
                    
                    <Col xs={24} md={12}>
                      <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: 8 }}>
                        <Text strong>Yard Information</Text>
                        <div style={{ marginTop: 8 }}>
                          <div><Text type="secondary">Yard Name:</Text> <Text strong>{yardDetails.yardName || 'N/A'}</Text></div>
                          <div><Text type="secondary">Yard ID:</Text> <Text>{yard.yardId || 'N/A'}</Text></div>
                          {yardDetails.yardAddress && (
                            <div><Text type="secondary">Location:</Text> <Text>
                              {yardDetails.yardAddress.address || ''}
                              {yardDetails.yardAddress.city && `, ${yardDetails.yardAddress.city}`}
                              {yardDetails.yardAddress.state && `, ${yardDetails.yardAddress.state}`}
                            </Text></div>
                          )}
                        </div>
                      </div>
                    </Col>
                    
                    <Col xs={24} md={12}>
                      <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: 8 }}>
                        <Text strong>Contact Information</Text>
                        <div style={{ marginTop: 8 }}>
                          <div><Text type="secondary">Contact Person:</Text> <Text>
                            {getAssignmentOrPrimaryYardValue(yard.contactPerson, yardDetails.contactPerson) || 'N/A'}
                          </Text></div>
                          <div><Text type="secondary">Phone:</Text> <Text>
                            {getAssignmentOrPrimaryYardValue(yard.contactNumber, yardDetails.contactNumbers) || 'N/A'}
                            {getAssignmentOrPrimaryYardValue(yard.contactPersonExtension, yardDetails.contactPersonExtension)
                              ? ` Ext: ${getAssignmentOrPrimaryYardValue(yard.contactPersonExtension, yardDetails.contactPersonExtension)}`
                              : ''}
                          </Text></div>
                          <div><Text type="secondary">Email:</Text> <Text>
                            {getAssignmentOrPrimaryYardValue(yard.contactEmail, yardDetails.email) || 'N/A'}
                          </Text></div>
                        </div>
                      </div>
                    </Col>
                    
                    <Col span={24}>
                      <div style={{ background: '#fff7e6', padding: '12px', borderRadius: 8 }}>
                        <Row gutter={[16, 16]}>
                          <Col xs={12} sm={6}>
                            <div><Text type="secondary">Car Parts Price:</Text></div>
                            <Text strong style={{ color: '#fa8c16', fontSize: 16 }}>
                              ${yard.originalPrice?.toFixed(2) || '0.00'}
                            </Text>
                          </Col>
                          <Col xs={12} sm={6}>
                            <div><Text type="secondary">Yard Price:</Text></div>
                            <Text strong style={{ color: '#722ed1', fontSize: 16 }}>
                              ${yard.price?.toFixed(2) || '0.00'}
                            </Text>
                          </Col>
                          <Col xs={12} sm={6}>
                            <div><Text type="secondary">Own Shipping:</Text></div>
                            <Text strong>${yard.ownShipping?.toFixed(2) || '0.00'}</Text>
                          </Col>
                          <Col xs={12} sm={6}>
                            <div><Text type="secondary">Yard Shipping:</Text></div>
                            <Text strong>${yard.yardShipping?.toFixed(2) || '0.00'}</Text>
                          </Col>
                          <Col xs={12} sm={6}>
                            <div><Text type="secondary">Core Charges:</Text></div>
                            <Text strong>${yard.coreCharges?.toFixed(2) || '0.00'}</Text>
                          </Col>
                          <Col xs={12} sm={6}>
                            <div><Text type="secondary">Final Total:</Text></div>
                            <Text strong style={{ color: '#52c41a', fontSize: 18 }}>
                              ${yard.finalPrice?.toFixed(2) || '0.00'}
                            </Text>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                    
                    {yard.description && (
                      <Col span={24}>
                        <Alert
                          message="Description"
                          description={yard.description}
                          type="info"
                          showIcon
                          style={{ borderRadius: 8 }}
                        />
                      </Col>
                    )}
                  </Row>
                </Card>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </div>
    );
  };

  // Handle expand/collapse
  const handleExpand = (expanded, record) => {
    if (expanded) {
      setExpandedRowKeys([record._id]);
    } else {
      setExpandedRowKeys([]);
    }
  };

  // Calculate statistics
  const calculateStatistics = (data) => {
    const totalPending = data.length;
    const totalAmount = data.reduce((sum, lead) => sum + (lead.totalAmount || 0), 0);
    const averageAmount = totalPending > 0 ? totalAmount / totalPending : 0;
    const completedCount = data.filter(lead => lead.zohoStatus === 'COMPLETED').length;
    const pendingCount = data.filter(lead => lead.zohoStatus !== 'COMPLETED').length;
    
    setStatistics({
      totalPending,
      totalAmount,
      averageAmount,
      completedCount,
      pendingCount,
    });
  };

  // Get date range based on quick filter
  const getQuickDateRange = (filterType) => {
    const now = dayjs();
    let startDate = now;
    let endDate = now;

    switch (filterType) {
      case 'today':
        startDate = now.startOf('day');
        endDate = now.endOf('day');
        break;
      case 'yesterday':
        startDate = now.subtract(1, 'day').startOf('day');
        endDate = now.subtract(1, 'day').endOf('day');
        break;
      case 'thisWeek':
        startDate = now.startOf('week');
        endDate = now.endOf('week');
        break;
      case 'thisMonth':
        startDate = now.startOf('month');
        endDate = now.endOf('month');
        break;
      case 'lastMonth':
        startDate = now.subtract(1, 'month').startOf('month');
        endDate = now.subtract(1, 'month').endOf('month');
        break;
      case 'thisQuarter':
        startDate = now.startOf('quarter');
        endDate = now.endOf('quarter');
        break;
      case 'thisYear':
        startDate = now.startOf('year');
        endDate = now.endOf('year');
        break;
      default:
        return null;
    }

    return [startDate, endDate];
  };

  const fetchYardDetails = async (yardId) => {
    if (!yardId || yardDetailsMap[yardId]) return;
    
    try {
      const response = await API.GetYardById(yardId);
      if (response?.status || response?.success) {
        const yardData = response.data || response;
        setYardDetailsMap(prev => ({
          ...prev,
          [yardId]: yardData
        }));
      }
    } catch (error) {
      console.error('Error fetching yard details:', error);
    }
  };

  // Fetch yard details for all leads when leads change
  useEffect(() => {
    const fetchAllYardDetails = async () => {
      const yardIds = new Set();
      
      leads.forEach(lead => {
        const yards = lead.yard || [];
        const latestYard = yards.length > 0 ? yards[yards.length - 1] : null;
        if (latestYard?.yardId && !yardDetailsMap[latestYard.yardId]) {
          yardIds.add(latestYard.yardId);
        }
      });

      for (const yardId of yardIds) {
        try {
          const response = await API.GetYardById(yardId);
          if (response?.status || response?.success) {
            const yardData = response.data || response;
            setYardDetailsMap(prev => ({
              ...prev,
              [yardId]: yardData
            }));
          }
        } catch (error) {
          console.error('Error fetching yard details:', error);
        }
      }
    };

    if (leads.length > 0) {
      fetchAllYardDetails();
    }
  }, [leads]);

  useEffect(() => {
    const defaultDateRange = getQuickDateRange('thisMonth');
    setDateRange(defaultDateRange);
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    maxTotalRef.current = 0;
  }, [searchQuery, effectiveSearchFields, dateRange, filteredInfo, sortedInfo.field, sortedInfo.order, selectedAgentId]);

  // Handle quick date filter change
  const handleQuickDateFilter = (value) => {
    setQuickDateFilter(value);
    const newDateRange = getQuickDateRange(value);
    setDateRange(newDateRange);
    setCurrentPage(1);
  };

  const fetchLeads = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const isSearching = Boolean(searchQuery);
      const workflowPurchaseOrderStatuses = ['PO-SENT', 'PO-NOT-SENT', 'PO-NOT-CONFIRMED'];
      const params = {
        payementAllow: 'Yes',
        purchaseOrderStatus: workflowPurchaseOrderStatuses.join(','),
        // Canceled orders live on the Customer Cancel / Refund pages, not here
        excludeCanceledOrders: 'true',
        page: currentPage,
        limit: pageSize,
        sortBy: 'lastPaymentDate',
        order: 'desc'
      };

      if (isSearching) {
        params.search = searchQuery;
        params.searchFields = searchFields;
      } else {
        params.dateField = 'lastPaymentDate';

       if (dateRange && dateRange[0] && dateRange[1] && dateRange[0].isValid() && dateRange[1].isValid()) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
    } else {
        // Default to this month if dateRange is invalid
        const now = dayjs();
        params.startDate = now.startOf('month').format('YYYY-MM-DD');
        params.endDate = now.endOf('month').format('YYYY-MM-DD');
    }
      }

      if (filteredInfo.part) {
        params.part = filteredInfo.part.join(',');
      }

      if (filteredInfo.zohoStatus) {
        params.zohoStatus = filteredInfo.zohoStatus.join(',');
      }

      if (filteredInfo.invoiceStatus) {
        params.invoiceStatus = filteredInfo.invoiceStatus.join(',');
      }

      if (filteredInfo.purchaseOrderStatus) {
        const narrowedStatuses = filteredInfo.purchaseOrderStatus.filter((status) =>
          workflowPurchaseOrderStatuses.includes(status)
        );
        params.purchaseOrderStatus = narrowedStatuses.length
          ? narrowedStatuses.join(',')
          : '__NO_MATCH__';
      }

      // Add agent filter for admin
      if (isAdmin && selectedAgentId) {
        params.agentId = selectedAgentId;
      }
      
      // The server sends only the fields this page uses
      // (LeadController LEAD_VIEW_FIELDS['order-process']).
      params.view = 'order-process';
      const response = await API.leadGetAll(params);

      if (response?.status && response.data) {
        const filteredLeads = response.data;

        setLeads(filteredLeads);
        calculateStatistics(filteredLeads);
        setTotalRecords(response.pagination?.totalRecords ?? filteredLeads.length);

        if (!isSearching && filteredLeads.length === 0 && dateRange && dateRange[0] && dateRange[1]) {
          message.info('No leads found with  Payment Paided = Yes in the selected last payment date range');
        }
        
      } else {
        setLeads([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      message.error('Failed to fetch leads');
      setLeads([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchLeads(true);
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
  };

  // Handle date range change
const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1] && dates[0].isValid() && dates[1].isValid()) {
        setDateRange(dates);
        setQuickDateFilter('custom');
    } else {
        // Reset to default (this month) if cancelled or invalid
        const defaultRange = getQuickDateRange('thisMonth');
        setDateRange(defaultRange);
        setQuickDateFilter('thisMonth');
        message.info('Date filter reset to this month');
    }
    setCurrentPage(1);
};
  // Handle clear filters
  const handleClearFilters = () => {
    setSearchText('');
    const defaultDateRange = getQuickDateRange('thisMonth');
    setDateRange(defaultDateRange);
    setQuickDateFilter('thisMonth');
    setFilteredInfo({ part: null, zohoStatus: null, invoiceStatus: null, purchaseOrderStatus: null, agent: null });
    setSelectedAgentId(null);
    setSortedInfo({ field: 'orderDate', order: 'descend' });
    setCurrentPage(1);
    maxTotalRef.current = 0;
    message.info({
      content: "All filters cleared",
      icon: <ClearOutlined />
    });
  };

  // Handle view button click
  const handleView = (record) => {
    setSelectedLeadId(record._id);
    navigate(`/lead-details/${record._id}`, { state: { sourceTab: 'order-process' } });
  };

  // Handle table change
  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  // Handle pagination change
  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };
const handleEditStart = (record, field) => {
  if (requiresYardForStatus(field) && !hasYardAssigned(record)) {
    showMissingYardError();
    return;
  }

  if (requiresOrderServiceAgentForStatus(field) && !hasOrderServiceAgentAssigned(record)) {
    showMissingOrderServiceAgentError();
    return;
  }

  // Get the latest date for this status from history
  const historyField = 
    field === 'zohoStatus' ? 'zohoStatusHistory' :
    field === 'invoiceStatus' ? 'invoiceStatusHistory' :
    field === 'purchaseOrderStatus' ? 'purchaseOrderStatusHistory' : null;
  
  // Handle relocate status date
  if (field === 'relocateStatus') {
    const existingDate = record.relocateStatusUpdatedAt || new Date();
    setEditingRelocatedDate(dayjs(existingDate));
  }
  
  let existingDate = null;
  if (historyField) {
    const history = record[historyField] || [];
    const currentStatus = record[field];
    const statusEntries = history.filter(entry => entry.status === currentStatus);
    if (statusEntries.length > 0) {
      const sortedEntries = [...statusEntries].sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt));
      existingDate = sortedEntries[0]?.changedAt;
    }
  }
  
  setEditingId(record._id);
  setEditingField(field);
  setEditingValue(record[field] || '');
  setEditingInvoiceDate(existingDate ? dayjs(existingDate) : dayjs());
};

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingId(null);
    setEditingField(null);
    setEditingValue('');
  };
const handleStatusChange = (record, field, value) => {
  if (requiresYardForStatus(field) && !hasYardAssigned(record)) {
    setEditingValue(record?.[field] || '');
    showMissingYardError();
    return;
  }

  if (requiresOrderServiceAgentForStatus(field) && !hasOrderServiceAgentAssigned(record)) {
    setEditingValue(record?.[field] || '');
    showMissingOrderServiceAgentError();
    return;
  }

  setEditingValue(value);
};

const handleEditSave = async (id, field, record = null) => {
  const currentLead = record || leads.find(lead => lead._id === id);

  if (requiresYardForStatus(field) && !hasYardAssigned(currentLead)) {
    setEditingValue(currentLead?.[field] || '');
    showMissingYardError();
    return;
  }

  if (requiresOrderServiceAgentForStatus(field) && !hasOrderServiceAgentAssigned(currentLead)) {
    setEditingValue(currentLead?.[field] || '');
    showMissingOrderServiceAgentError();
    return;
  }

  // For relocate status, validate the date
  if (field === 'relocateStatus') {
    if (!editingRelocatedDate) {
      message.warning('Please select a date for the relocate status change');
      return;
    }
  } else {
    // For other statuses, validate date
    if (!editingInvoiceDate) {
      message.warning('Please select a date');
      return;
    }
  }

  try {
    const historyField = 
      field === 'zohoStatus' ? 'zohoStatusHistory' :
      field === 'invoiceStatus' ? 'invoiceStatusHistory' :
      field === 'purchaseOrderStatus' ? 'purchaseOrderStatusHistory' : null;
    
    let updatePayload = {};
    updatePayload[field] = editingValue;
    
    // Handle relocate status separately
    if (field === 'relocateStatus') {
      updatePayload.relocateStatusUpdatedAt = editingRelocatedDate.toISOString();
    }
    
    // Handle history for document statuses
    if (historyField) {
      const existingHistory = currentLead?.[historyField] || [];
      
      // Check if there's already an entry with this status
      const existingEntryIndex = existingHistory.findIndex(
        entry => entry.status === editingValue
      );
      
      let updatedHistory;
      
      if (existingEntryIndex !== -1) {
        // Update existing entry's date
        updatedHistory = [...existingHistory];
        updatedHistory[existingEntryIndex] = {
          ...updatedHistory[existingEntryIndex],
          changedAt: editingInvoiceDate.toISOString()
        };
      } else {
        // Add new entry
        const newHistoryEntry = {
          status: editingValue,
          changedAt: editingInvoiceDate.toISOString()
        };
        updatedHistory = [...existingHistory, newHistoryEntry];
      }
      
      updatePayload[historyField] = updatedHistory;
      updatePayload._skipAutoHistory = true;
    }
    
    const response = await API.UpdateLead(id, updatePayload);
    
    if (response.status) {
      message.success(`${field === 'relocateStatus' ? 'Relocate status' : field} updated successfully!`);
      setEditingId(null);
      setEditingField(null);
      setEditingValue('');
      setEditingInvoiceDate(null);
      setEditingRelocatedDate(null);
      await fetchLeads();
    } else {
      message.error('Failed to update status');
    }
  } catch (error) {
    console.error('Error updating status:', error);
    message.error('Error updating status');
  }
};
// Handle Tracking Status Edit
const handleTrackingEdit = (record) => {
  const tracking = record.ShipmentTracking || {};
  setSelectedTrackingRecord(record);
  trackingForm.setFieldsValue({
    picturesApproval: tracking?.picturesApproval || 'No',
    picturesStatus: tracking?.picturesStatus || 'Not Received',
  });
  setTrackingModalVisible(true);
};

// Handle Tracking Save
const handleTrackingSave = async () => {
  try {
    setUpdatingTracking(true);
    const values = await trackingForm.validateFields();
    
    const tracking = selectedTrackingRecord?.ShipmentTracking || {};
    
    const payload = {
      ShipmentTracking: {
        ...tracking,
        picturesApproval: values.picturesApproval,
        picturesStatus: values.picturesStatus,
        ShipmentTrackingUpdatedAt: new Date().toISOString()
      }
    };
    
    const response = await API.UpdateLead(selectedTrackingRecord._id, payload);
    
    if (response?.status || response?.success) {
      message.success("Pictures status updated successfully!");
      setTrackingModalVisible(false);
      fetchLeads(); // Refresh the leads data
    } else {
      message.error(response?.message || "Failed to update pictures status");
    }
  } catch (error) {
    console.error("Error updating pictures status:", error);
    message.error("Failed to update pictures status");
  } finally {
    setUpdatingTracking(false);
  }
};

// Customer cancels while the order is still here. Saves the same cancel fields as
// Shipment Tracking's "Edit Shipment Details" popup when Part Status = "Customer
// Canceled Order" (Order Accepted = "Customer Canceled"; the backend writes the same
// history), without the shipment details this order doesn't have yet. Dated today.
// PO and Priority are not changed.
const handleCustomerCancel = (record) => {
  Modal.confirm({
    title: 'Cancel this order?',
    content: (
      <div>
        <p style={{ margin: '0 0 6px' }}>
          This order will be removed from the Order Process page and moved to these pages:
        </p>
        <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
          <li>Customer Cancel</li>
          <li>Customer Refund</li>
          <li>Yard Refund</li>
        </ul>
        <p style={{ margin: 0 }}>
          You can undo it later from Lead Details → Order Process Complete.
        </p>
      </div>
    ),
    okText: 'Yes, Cancel Order',
    okButtonProps: { type: 'primary', danger: true },
    cancelText: 'Keep Order',
    onOk: async () => {
      try {
        const payload = {
          ShipmentTracking: {
            ...record.ShipmentTracking,
            partStatus: 'Customer Canceled Order',
          },
          ShipmentTrackingUpdatedAt: new Date().toISOString(),
          orderProcessComplete: {
            ...record.orderProcessComplete,
            partStatus: 'Customer Canceled',
          },
        };

        const response = await API.UpdateLead(record._id, payload);

        if (response?.status || response?.success) {
          message.success('Order canceled');
          fetchLeads();
        } else {
          message.error(response?.message || 'Failed to cancel the order');
        }
      } catch (error) {
        console.error('Error canceling order:', error);
        message.error('Failed to cancel the order');
      }
    },
  });
};
  // Load leads on mount and when dependencies change
  useEffect(() => {
    fetchLeads();
  }, [
    currentPage, 
    pageSize, 
    sortedInfo.field, 
    sortedInfo.order, 
    filteredInfo.part,
    filteredInfo.zohoStatus,
    filteredInfo.invoiceStatus,
    filteredInfo.purchaseOrderStatus,
    searchQuery,
    effectiveSearchFields,
    dateRange,
    selectedAgentId,
    selectedServiceAgentId
  ]);

  // Add this helper function near your other helper functions
  const getLatestStatusDate = (historyArray, statusValue) => {
    if (!historyArray || historyArray.length === 0) return null;
    
    // Find entries with matching status
    const statusEntries = historyArray.filter(entry => entry.status === statusValue);
    
    if (statusEntries.length === 0) return null;
    
    // Sort by changedAt and get the most recent
    const sortedEntries = [...statusEntries].sort(
      (a, b) => new Date(b.changedAt) - new Date(a.changedAt)
    );
    
    return sortedEntries[0]?.changedAt;
  };

  // Status color mapping
  const getStatusConfig = (status, type) => {
    const configs = {
      zoho: {
        'SENT': { color: '#1890ff', icon: <SyncOutlined />, text: 'SENT' },
        'NOT-SENT': { color: '#bfbfbf', icon: <CloseOutlined />, text: 'NOT SENT' },
        'COMPLETED': { color: '#52c41a', icon: <CheckOutlined />, text: 'COMPLETED' },
        'INCOMPLETE': { color: '#faad14', icon: <StopOutlined />, text: 'INCOMPLETE' },
        'FAILED': { color: '#ff4d4f', icon: <CloseOutlined />, text: 'FAILED' },
        'RECEIVED': { color: '#722ed1', icon: <FileDoneOutlined />, text: 'RECEIVED' },
        'DL-COPY': { color: '#13c2c2', icon: <FileTextOutlined />, text: 'DL COPY' },
        'EMAIL-APPROVAL': { color: '#eb2f96', icon: <MailOutlined />, text: 'EMAIL APPROVAL' },
        'default': { color: '#bfbfbf', icon: <FileDoneOutlined />, text: 'NOT-SENT' }
      },
      invoice: {
        'INVOICE-SENT': { color: '#52c41a', icon: <CheckOutlined />, text: 'INVOICE SENT' },
        'INVOICE-NOT-SENT': { color: '#bfbfbf', icon: <CloseOutlined />, text: 'NOT SENT' },
        'default': { color: '#bfbfbf', icon: <FileTextOutlined />, text: 'NOT SENT' }
      },
      po: {
        'PO-SENT': { color: '#52c41a', icon: <CheckOutlined />, text: 'PO SENT' },
        'PO-NOT-SENT': { color: '#bfbfbf', icon: <CloseOutlined />, text: 'NOT SENT' },
        'PO-CONFIRMED': { color: '#1890ff', icon: <CheckOutlined />, text: 'CONFIRMED' },
        'PO-NOT-CONFIRMED': { color: '#faad14', icon: <StopOutlined />, text: 'NOT CONFIRMED' },
        'default': { color: '#bfbfbf', icon: <ShoppingCartOutlined />, text: 'NOT SENT' }
      }
    };

    return configs[type]?.[status] || configs[type]?.default;
  };

  const DOCUMENT_STATUS_FIELD_LABELS = {
    zohoStatus: 'ZOHO Status',
    invoiceStatus: 'Invoice Status',
    purchaseOrderStatus: 'PO Status'
  };

  const getUserDisplayName = (user) => {
    if (!user || typeof user !== 'object') return null;
    return (
      user.fullName ||
      user.name ||
      `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
      user.userName ||
      null
    );
  };

  const handleViewStatusHistory = (record, field, historyField) => {
    const history = record[historyField] || [];
    const sortedEntries = [...history].sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt));
    setStatusHistoryModalTitle(DOCUMENT_STATUS_FIELD_LABELS[field] || 'Status History');
    setStatusHistoryModalEntries(sortedEntries);
    setStatusHistoryModalVisible(true);
  };

const renderEditableCell = (record, field, options, type, historyField) => {
  const isEditing = editingId === record._id && editingField === field;
  const currentStatus = record[field];

  // Get the OLDEST entry for this status (first occurrence)
  const getFirstStatusEntry = () => {
    const history = record[historyField] || [];
    const statusEntries = history.filter(entry => entry.status === currentStatus);
    if (statusEntries.length === 0) return null;
    // Sort ascending (oldest first) and take the first one
    const sortedEntries = [...statusEntries].sort((a, b) => new Date(a.changedAt) - new Date(b.changedAt));
    return sortedEntries[0];
  };

  const firstStatusEntry = getFirstStatusEntry();
  const statusDate = firstStatusEntry?.changedAt;  // ✅ Use statusDate, not latestDate
  const changedByName = getUserDisplayName(firstStatusEntry?.changedBy);
  const hasHistory = (record[historyField] || []).length > 0;

  const CARD_HEADERS = {
    zoho: { icon: <SyncOutlined style={{ fontSize: 12, color: '#1890ff' }} />, label: 'Zoho Sign' },
    invoice: { icon: <FileTextOutlined style={{ fontSize: 12, color: '#13c2c2' }} />, label: 'Invoice' },
    po: { icon: <ShoppingCartOutlined style={{ fontSize: 12, color: '#722ed1' }} />, label: 'Purchase Order' }
  };
  const cardHeader = CARD_HEADERS[type] || CARD_HEADERS.zoho;

  const headerRow = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        {cardHeader.icon}
        <Text
          strong
          style={{
            fontSize: 11,
            color: '#8c8c8c',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {cardHeader.label}
        </Text>
      </div>
      {type === 'po' && (
        <Button
          size="small"
          icon={<MailOutlined />}
          onClick={() => handleOpenPOModal(record)}
          block
          style={{
            color: '#722ed1',
            borderColor: '#722ed1',
            fontSize: 11,
            fontWeight: 500,
            height: '26px'
          }}
        >
          Email PO
        </Button>
      )}
    </div>
  );

  if (isEditing) {
    return (
      <div>
        {headerRow}
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Select
          value={editingValue}
          onChange={(value) => handleStatusChange(record, field, value)}
          style={{ width: '100%' }}
          size="small"
          showSearch
          optionFilterProp="children"
          autoFocus
        >
          {options.map(option => (
            <Option key={option} value={option}>{option.replace(/-/g, ' ')}</Option>
          ))}
        </Select>
        <DatePicker
          value={editingInvoiceDate}
          onChange={setEditingInvoiceDate}
          format="MM/DD/YYYY"
          size="small"
          placeholder="Select date"
          style={{ width: '100%' }}
        />
        <Space size={4}>
          <Button 
            type="primary" 
            size="small" 
            icon={<SaveOutlined />} 
            onClick={() => handleEditSave(record._id, field, record)}
            style={{ borderRadius: 6, minWidth: 'auto', padding: '0 8px' }}
          />
          <Button
            size="small"
            icon={<CloseOutlined />}
            onClick={handleEditCancel}
            style={{ borderRadius: 6, minWidth: 'auto', padding: '0 8px' }}
          />
        </Space>
        </Space>
      </div>
    );
  }

  const config = getStatusConfig(record[field], type);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {headerRow}
      {/* Status - prominent, full width */}
      <Tag
        icon={config.icon}
        color={config.color}
        style={{
          padding: '5px 12px',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.3,
          cursor: 'pointer',
          margin: 0,
          width: '100%',
          textAlign: 'center',
          boxSizing: 'border-box'
        }}
        onClick={() => handleEditStart(record, field)}
      >
        {config.text}
      </Tag>

      {/* Actions row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
        {hasHistory && (
          <Tooltip title="Click to view change history">
            <Button
              type="text"
              size="small"
              icon={<HistoryOutlined style={{ fontSize: '12px' }} />}
              onClick={() => handleViewStatusHistory(record, field, historyField)}
              style={{
                color: '#8c8c8c',
                padding: '4px',
                minWidth: 'auto',
                height: '24px'
              }}
            />
          </Tooltip>
        )}
        <Tooltip title="Edit Status with Date">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditStart(record, field)}
            style={{
              color: '#1890ff',
              padding: '4px',
              minWidth: 'auto',
              height: '24px'
            }}
          />
        </Tooltip>
      </div>

      {/* Status date and changed-by, each on its own line */}
      {statusDate && (
        <Tooltip title={`Changed on ${formatToPST(statusDate)}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingTop: '2px', borderTop: '1px dashed #e8e8e8' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <ClockCircleOutlined style={{ fontSize: '11px', color: '#bfbfbf' }} />
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {dayjs(statusDate).format('MM/DD/YY hh:mm A')}
              </Text>
            </div>
            {changedByName && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <UserOutlined style={{ fontSize: '11px', color: '#bfbfbf' }} />
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {changedByName}
                </Text>
              </div>
            )}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

  const isSearching = searchText?.trim().length > 0;

  // Agent column definition
  const agentColumn = isAdmin ? [{
    title: 'Sales-Agent',
    key: 'agent',
    width: 180,
    sorter: (a, b) => getAgentName(a).localeCompare(getAgentName(b)),
    render: (_, record) => {
      const agentName = getAgentName(record);
      const isUnassigned = agentName === 'Unassigned';
      return (
        <Tooltip title={agentName}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar
              size={32}
              icon={<UserOutlined />}
              style={{
                backgroundColor: isUnassigned ? '#d9d9d9' : '#722ed1',
                flexShrink: 0,
              }}
            >
              {!isUnassigned && agentName?.[0]?.toUpperCase()}
            </Avatar>
            <Text
              ellipsis={{ tooltip: true }}
              style={{
                fontSize: 13,
                color: isUnassigned ? '#8c8c8c' : '#1f2937',
                fontStyle: isUnassigned ? 'italic' : 'normal',
              }}
            >
              {agentName}
            </Text>
          </div>
        </Tooltip>
      );
    },
  }] : [];
  
  function formatToPST(isoDateString) {
    if (!isoDateString) return 'N/A';
    const date = new Date(isoDateString);
    const options = {
      timeZone: 'America/Los_Angeles',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };
    return date.toLocaleString('en-US', options);
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getLatestPaymentYesDate = (record) => {
    const yesEntries = record.payementAllowHistory?.filter(
      history => history.value === 'Yes'
    );
    
    if (!yesEntries || yesEntries.length === 0) return null;
    
    // Sort by changedAt and get the most recent
    const sortedYesEntries = [...yesEntries].sort(
      (a, b) => new Date(b.changedAt) - new Date(a.changedAt)
    );
    
    return sortedYesEntries[0]?.changedAt;
  };

  const getLastPaymentDate = (record) => {
    const payments = Array.isArray(record?.advancePayments)
      ? record.advancePayments.filter((payment) => payment?.date)
      : [];

    if (payments.length === 0) return null;

    const latestPayment = [...payments].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    )[0];

    return latestPayment?.date || null;
  };

const columns = [
  {
    title: 'Order Number',
    width: 112,
    key: 'orderNumber',
    render: (_, record) => {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {record.orderNumber ? (
            <>
              <FileTextOutlined style={{ color: '#722ed1', fontSize: 14 }} />
              <Text strong style={{ 
                color: '#722ed1', 
                fontSize: 13,
                fontFamily: 'monospace'
              }}>
                #{record.orderNumber.slice(-8)}
              </Text>
              <Tooltip title="Copy Order Number">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined style={{ fontSize: 12 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(record.orderNumber);
                    message.success('Order number copied!');
                  }}
                  style={{
                    padding: 0,
                    height: 20,
                    width: 20,
                    minWidth: 20,
                    color: '#722ed1',
                  }}
                />
              </Tooltip>
            </>
          ) : (
            <>
              <ClockCircleOutlined style={{ color: '#ff4d4f', fontSize: 14 }} />
              <Text style={{ 
                color: '#ff4d4f', 
                fontSize: 12,
                fontStyle: 'italic'
              }}>
                Waiting..
              </Text>
            </>
          )}
        </div>
      );
    }
  },
  ...agentColumn,
    {
    title: 'Order Service-Agent',
    key: 'serviceAgent',
    width: 180,
    sorter: (a, b) => {
        const nameA = getServiceAgentName(a) || '';
        const nameB = getServiceAgentName(b) || '';
        return nameA.localeCompare(nameB);
    },
    render: (_, record) => {
        const serviceAgentName = getServiceAgentName(record);
        const isAssigned = !!serviceAgentName;
        
        return (
            <div 
                style={{ cursor: 'pointer' }}
                onClick={() => handleServiceAgentEdit(record)}
            >
                <Tooltip title={isAssigned ? `Click to change service agent` : 'Click to assign service agent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar
                            size={32}
                            icon={<TeamOutlined />}
                            style={{
                                backgroundColor: isAssigned ? '#52c41a' : '#f0f0f0',
                                flexShrink: 0,
                            }}
                        >
                            {isAssigned && serviceAgentName?.[0]?.toUpperCase()}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                            {isAssigned ? (
                                <>
                                    <Text
                                        ellipsis={{ tooltip: true }}
                                        style={{
                                            fontSize: 13,
                                            color: '#1f2937',
                                            display: 'block',
                                            fontWeight: 500
                                        }}
                                    >
                                        {serviceAgentName}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 10 }}>
                                        Click to edit
                                    </Text>
                                </>
                            ) : (
                                <Text
                                    style={{
                                        fontSize: 13,
                                        color: '#1890ff',
                                        fontStyle: 'italic'
                                    }}
                                >
                                    + Assign Agent
                                </Text>
                            )}
                        </div>
                        <EditOutlined 
                            style={{ 
                                fontSize: 12, 
                                color: '#1890ff',
                                opacity: 0.6
                            }} 
                        />
                    </div>
                </Tooltip>
            </div>
        );
    },
},
  
  {
    title: 'Customer',
    key: 'customer',
    fixed: 'left',
    width: 250,
    sorter: true,
    sortOrder: sortedInfo.field === 'customer' && sortedInfo.order,
    render: (_, record) => (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
        <Avatar 
          size={38}
          icon={<UserOutlined />} 
          style={{ 
            background: `linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)`,
            boxShadow: '0 4px 12px rgba(24,144,255,0.2)',
            flexShrink: 0
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            strong
            ellipsis={{ tooltip: record.personalInfo?.fullName || 'N/A' }}
            style={{ fontSize: 14, display: 'block', marginBottom: 4 }}
          >
            {record.personalInfo?.fullName || 'N/A'}
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
              <MailOutlined style={{ color: '#1890ff', fontSize: 11 }} />
              <Text
                type="secondary"
                ellipsis={{ tooltip: record.personalInfo?.email || 'N/A' }}
                style={{ fontSize: 12, display: 'block', minWidth: 0 }}
              >
                {record.personalInfo?.email || 'N/A'}
              </Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
              <PhoneOutlined style={{ color: '#52c41a', fontSize: 11 }} />
              <Text
                type="secondary"
                ellipsis={{ tooltip: record.personalInfo?.phoneNumber || 'N/A' }}
                style={{ fontSize: 12, display: 'block', minWidth: 0 }}
              >
                {record.personalInfo?.phoneNumber || 'N/A'}
              </Text>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: 'Vehicle',
    key: 'vehicleInfo',
    width: 205,
    render: (_, record) => (
      <Card size="small" style={{ borderRadius: 10, background: '#fafafa', border: 'none' }} bodyStyle={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
            <CarOutlined style={{ color: '#faad14', fontSize: 12, marginTop: 2, flexShrink: 0 }} />
            <Text strong style={{ fontSize: 13, display: 'block', lineHeight: 1.25, wordBreak: 'break-word' }}>
              {record.vehicleInfo?.year || ''} {record.vehicleInfo?.make || ''}
            </Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 16, display: 'block', lineHeight: 1.25, wordBreak: 'break-word' }}>
            {record.vehicleInfo?.model || 'N/A'}
          </Text>
          {record.vehicleInfo?.part && (
            <Tag color="cyan" icon={<TagOutlined />} style={{ marginLeft: 16, marginTop: 2, fontSize: 10, whiteSpace: 'normal', lineHeight: 1.2 }}>
              {record.vehicleInfo.part}
            </Tag>
          )}
        </div>
      </Card>
    ),
  },
    {
    title: 'Specification',
    key: 'specification',
    width: 250,
    render: (_, record) => {
        const specification = record.vehicleInfo?.specification || '';
        const isEditing = editingSpecification === record._id;
        
        if (isEditing) {
            return (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        value={editingSpecValue}
                        onChange={(e) => setEditingSpecValue(e.target.value)}
                        placeholder="Enter specification details..."
                        style={{ flex: 1, fontSize: '12px' }}
                        autoFocus
                        maxLength={2000}
                        showCount
                    />
                    <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={updatingSpecification}
                            onClick={() => handleUpdateSpecification(record._id, editingSpecValue)}
                            style={{ minWidth: '32px' }}
                        />
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setEditingSpecification(null);
                                setEditingSpecValue('');
                            }}
                        />
                    </div>
                </div>
            );
        }
        
        return (
            <div 
                style={{ 
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    transition: 'all 0.3s',
                    backgroundColor: specification ? 'transparent' : '#fafafa',
                    border: '1px solid transparent',
                    minHeight: '60px'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d9d9d9';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = specification ? 'transparent' : '#fafafa';
                    e.currentTarget.style.borderColor = 'transparent';
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setEditingSpecification(record._id);
                    setEditingSpecValue(specification);
                }}
            >
                {specification ? (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <FileTextOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                            <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>Specification:</Text>
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word',
                            display: 'block',
                            lineHeight: '1.5'
                        }}>
                            {specification.length > 100 
                                ? `${specification.substring(0, 100)}...` 
                                : specification}
                        </Text>
                        <div style={{ marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <EditOutlined style={{ marginRight: '4px' }} />
                                Click to edit
                            </Text>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <FileTextOutlined style={{ marginRight: '4px', color: '#52c41a' }} />
                            <EditOutlined style={{ marginRight: '4px', fontSize: '10px' }} />
                            Click to add specification
                        </Text>
                    </div>
                )}
            </div>
        );
    },
}, 
    {
    title: 'Document Status',
    key: 'documentStatus',
    width: 360,
    onCell: () => ({
      style: {
        paddingRight: 20,
        borderRight: '1px solid #eef0f2'
      }
    }),
    render: (_, record) => (
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        {/* ZOHO Status */}
        <div style={{
          padding: '8px 10px',
          background: '#fafafa',
          borderRadius: 8,
          border: '1px solid #f0f0f0'
        }}>
          {renderEditableCell(record, 'zohoStatus', zohoStatusOptions, 'zoho', 'zohoStatusHistory')}
        </div>

        {/* INVOICE Status */}
        <div style={{
          padding: '8px 10px',
          background: '#fafafa',
          borderRadius: 8,
          border: '1px solid #f0f0f0'
        }}>
          {renderEditableCell(record, 'invoiceStatus', invoiceStatusOptions, 'invoice', 'invoiceStatusHistory')}
        </div>

        {/* PO Status */}
        <div style={{
          padding: '8px 10px',
          background: '#fafafa',
          borderRadius: 8,
          border: '1px solid #f0f0f0'
        }}>
          {renderEditableCell(record, 'purchaseOrderStatus', purchaseOrderStatusOptions, 'po', 'purchaseOrderStatusHistory')}
        </div>
      </Space>
    ),
  },
{
    title: 'Yard',
    key: 'yard',
    width: 300,
      align: 'center',  // <-- ADD THIS
    onCell: () => ({
      style: {
        // paddingLeft: 20,
        background: '#fafbfc'
      }
    }),
    onHeaderCell: () => ({
      style: {
        background: '#fafbfc'
      }
    }),
    render: (_, record) => {
        const latestYard = record.yard?.length > 0
            ? record.yard[record.yard.length - 1]
            : null;
        const yardDetails = latestYard?.yardId
            ? yardDetailsMap[latestYard.yardId]
            : null;

        if (!latestYard) {
            return (
                <div>
                    <Space>
                        <Text type="secondary">No yard assigned</Text>
                        <Tooltip title="View Yard Details">
                            <Button
                                type="text"
                                size="small"
                                shape="circle"
                                icon={<EyeOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewYardDetails(record);
                                }}
                            />
                        </Tooltip>
                    </Space>
                    <div style={{ marginTop: 8 }}>
                        <Button
                            type="link"
                            size="small"
                            icon={<ShopOutlined />}
                            onClick={() => handleReAssignYard(record)}
                            style={{ padding: 0 }}
                        >
                            Assign Yard
                        </Button>
                    </div>
                </div>
            );
        }

        const isVerified = latestYard.verifiedAmountStats === 'YES';
        const verifiedPrice = latestYard.verifiedAmountPrice || 0;
        const finalPrice = latestYard.finalPrice || latestYard.price || 0;
        const priceDifference = verifiedPrice - finalPrice;
        const hasDifference = priceDifference !== 0;

        return (
            <div>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Space>
                        <ShopOutlined style={{ color: '#722ed1' }} />
                        <Text strong>
                            {yardDetails?.yardName || 'Loading...'}
                        </Text>
                        <Tooltip title="View Yard Details">
                            <Button
                                type="text"
                                size="small"
                                shape="circle"
                                icon={<EyeOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewYardDetails(record);
                                }}
                            />
                        </Tooltip>
                    </Space>

                    <div style={{ fontSize: 12 }}>
                        <div>
                            💰 Price: ${finalPrice.toFixed(2)}
                        </div>
                        {latestYard.yardConfirmDate && (
                            <div>
                                📅 {dayjs(latestYard.yardConfirmDate).format('MM/DD/YYYY')}
                            </div>
                        )}
                    </div>

                    {/* Verification Status Display */}
                    <div style={{ marginTop: '4px' }}>
                        {isVerified ? (
                            <div>
                                <Space size={4} wrap>
                                    <Tag color="success" size="small" style={{ fontSize: '11px', margin: 0 }}>
                                        <CheckCircleOutlined /> Verified
                                    </Tag>
                                    {hasDifference ? (
                                        <Tag
                                            color={priceDifference > 0 ? 'orange' : 'red'}
                                            style={{ fontSize: '11px', margin: 0 }}
                                        >
                                            {priceDifference > 0 ? '▲' : '▼'} ${Math.abs(priceDifference).toFixed(2)} diff
                                        </Tag>
                                    ) : (
                                        <Tag color="green" style={{ fontSize: '11px', margin: 0 }}>
                                            ✓ Matches
                                        </Tag>
                                    )}
                                    <Tooltip title={`Verified: $${verifiedPrice.toFixed(2)} | Final: $${finalPrice.toFixed(2)} | Difference: ${priceDifference > 0 ? '+' : ''}${priceDifference.toFixed(2)}`}>
                                        <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '12px', cursor: 'pointer' }} />
                                    </Tooltip>
                                </Space>
                                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                    ✓ ${verifiedPrice.toFixed(2)}
                                </div>
                            </div>
                        ) : (
                            <Tag color="warning" size="small" style={{ fontSize: '11px' }}>
                                <ClockCircleOutlined /> Not Verified
                            </Tag>
                        )}
                    </div>

                    <Space direction="vertical" size={2} style={{ alignItems: 'flex-start' }}>
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleReAssignYard(record)}
                            style={{ padding: 0, fontSize: 12 }}
                        >
                            Re-assign Yard
                        </Button>
                        {record.yardReassignmentHistory?.length > 0 && (
                            <Tooltip title="View yard reassignment history">
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<HistoryOutlined />}
                                    onClick={() => setYardHistoryLead(record)}
                                    style={{ padding: 0, fontSize: 12 }}
                                >
                                    History ({record.yardReassignmentHistory.length})
                                </Button>
                            </Tooltip>
                        )}
                    </Space>
                </Space>
            </div>
        );
    }
},
{
  title: 'Relocated',
  key: 'relocated',
  width: 200,
  align: 'center',
  render: (_, record) => {
    const relocateStatus = record.relocateStatus || 'No';
    const relocateStatusUpdatedAt = record.relocateStatusUpdatedAt;
    const relocateUpdatedPST = relocateStatusUpdatedAt ? toPST(relocateStatusUpdatedAt) : null;
    const isEditing = editingId === record._id && editingField === 'relocateStatus';
    
    if (isEditing) {
      return (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Select
            value={editingValue}
            onChange={(value) => setEditingValue(value)}
            style={{ width: '100%' }}
            size="small"
          >
            <Option value="Yes">
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <span>Yes - Relocated</span>
              </Space>
            </Option>
            <Option value="No">
              <Space>
                <CloseCircleOutlined style={{ color: '#f5222d' }} />
                <span>No - Not Relocated</span>
              </Space>
            </Option>
          </Select>
          <DatePicker
            value={editingRelocatedDate}
            onChange={(date) => setEditingRelocatedDate(date)}
            size="small"
            placeholder="Select date"
            style={{ width: '100%' }}
            format="MM/DD/YYYY"
          />
          <Space size={4}>
            <Button 
              type="primary" 
              size="small" 
              icon={<CheckOutlined />} 
              onClick={() => handleEditSave(record._id, 'relocateStatus')}
              style={{ flex: 1, borderRadius: 6 }}
            />
            <Button 
              size="small" 
              icon={<CloseOutlined />} 
              onClick={() => {
                setEditingId(null);
                setEditingField(null);
                setEditingValue('');
                setEditingRelocatedDate(null);
              }}
              style={{ borderRadius: 6 }}
            />
          </Space>
        </Space>
      );
    }
    
    return (
      <div>
        <Space size={4}>
          <Tag 
            color={relocateStatus === 'Yes' ? 'success' : 'error'}
            icon={relocateStatus === 'Yes' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            style={{ 
              padding: '4px 12px', 
              borderRadius: 16, 
              fontWeight: 500, 
              cursor: 'pointer',
              margin: 0
            }}
            onClick={() => handleEditStart(record, 'relocateStatus')}
          >
            {relocateStatus === 'Yes' ? '✓ Yes' : '✗ No'}
          </Tag>
          <Tooltip title="Edit Relocate Status with Date">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEditStart(record, 'relocateStatus')}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
        </Space>
        {relocateUpdatedPST && (
          <div style={{ marginTop: 6 }}>
            <Tooltip title={`Last updated: ${relocateUpdatedPST.format('MMMM DD, YYYY hh:mm:ss A')} PST`}>
              <Space size={4}>
                <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {relocateUpdatedPST.format('MM/DD/YY · hh:mm A')}
                </Text>
              </Space>
            </Tooltip>
          </div>
        )}
      </div>
    );
  },
},
{
    title: 'Company Card Details',
    key: 'companyCardDetails',
    width: 220,
    render: (_, record) => (
        <CompanyCardCell 
            record={record} 
            isAdmin={isAdmin} 
            onRefresh={() => fetchLeads()} 
        />
    ),
},
{
    title: 'Customer Payment ID',
    key: 'customerPaymentId',
    width: 220,
    render: (_, record) => (
        <PaymentIdCell 
            record={record} 
            isAdmin={isAdmin} 
            onRefresh={() => fetchLeads()} 
        />
    ),
},
  {
    title: 'Amount',
    key: 'amount',
    width: 160,
    sorter: true,
    sortOrder: sortedInfo.field === 'totalAmount' && sortedInfo.order,
    render: (_, record) => {
      const totalAmount = record.totalAmount || 0;
      const paidAmount = record.paidAmount || 0;
      const balance = record.balanceDue || (totalAmount - paidAmount);
      
      // Get latest payment by date
      const advancePayments = record.advancePayments || [];
      const latestPayment = advancePayments.length > 0 
        ? advancePayments.sort((a, b) => new Date(b.date) - new Date(a.date))[0]
        : null;
      const latestPaymentDate = latestPayment?.date ? dayjs(latestPayment.date).tz('America/Los_Angeles') : null;
      
      return (
        <Card size="small" style={{ borderRadius: 10, background: '#fafafa', border: 'none' }} bodyStyle={{ padding: '8px 12px' }}>
          {/* Total Amount with Edit Button */}
          <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Space size={4}>
                <DollarOutlined style={{ color: '#1890ff', fontSize: 11 }} />
                <Text style={{ fontSize: 11 }}>Total:</Text>
                <Tag 
                  color={totalAmount > 1000 ? 'volcano' : totalAmount > 500 ? 'orange' : 'green'} 
                  style={{ borderRadius: 16, padding: '2px 8px', fontWeight: 600, margin: 0 }}
                >
                  {formatCurrency(totalAmount)}
                </Tag>
              </Space>
              {isAdmin && (
                <Tooltip title="Re-Quoted Amount">
                  <Button
                    size="small"
                    type="default"
                    style={{
                      fontSize: 10,
                      padding: '2px 8px',
                      borderRadius: 6,
                      borderColor: '#1890ff',
                      color: '#1890ff'
                    }}
                    onClick={() => handleAmountEditOpen(record)}
                  >
                    Re-Quoted
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
          
          {/* Paid Amount */}
          <div style={{ marginBottom: 6 }}>
            <Space size={4}>
              <CreditCardOutlined style={{ color: '#52c41a', fontSize: 11 }} />
              <Text style={{ fontSize: 11 }}>Paid:</Text>
              <Text strong style={{ fontSize: 12, color: '#52c41a' }}>{formatCurrency(paidAmount)}</Text>
            </Space>
          </div>
          
          {/* Balance */}
          <div style={{ marginBottom: latestPaymentDate ? 6 : 0 }}>
            <Space size={4}>
              <WalletOutlined style={{ color: balance > 0 ? '#fa8c16' : '#52c41a', fontSize: 11 }} />
              <Text style={{ fontSize: 11 }}>Balance:</Text>
              <Text strong style={{ 
                fontSize: 12, 
                color: balance > 0 ? '#fa8c16' : '#52c41a'
              }}>
                {formatCurrency(balance)}
              </Text>
            </Space>
          </div>
          
          {/* Latest payment with full date and time */}
          {latestPaymentDate && (
            <div style={{ 
              marginTop: 6,
              paddingTop: 6,
              borderTop: '1px dashed #e8e8e8'
            }}>
              <Tooltip title={`Amount: ${formatCurrency(latestPayment.amount)} • Method: ${latestPayment.paymentMethod} • Status: ${latestPayment.status}`}>
                <Space size={4} direction="vertical" style={{ width: '100%', gap: 2 }}>
                  <Space size={4}>
                    <ClockCircleOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      Last payment: {latestPaymentDate.format('MM/DD/YYYY · hh:mm A')}
                    </Text>
                  </Space>
                </Space>
              </Tooltip>
            </div>
          )}
        </Card>
      );
    },
  },

  {
    title: 'Date & Payment',
    key: 'datePayment',
    width: 280,
    sorter: (a, b) => {
      // Sort by payment approved date (primary)
      const dateA = getLatestPaymentYesDate(a);
      const dateB = getLatestPaymentYesDate(b);
      if (dateA && dateB) return new Date(dateB) - new Date(dateA);
      if (dateA) return -1;
      if (dateB) return 1;
      return 0;
    },
    defaultSortOrder: 'descend',
    render: (_, record) => {
      const paymentYesDate = getLatestPaymentYesDate(record);
      const createdAt = record.createdAt;
      const updatedAt = record.updatedAt;

      const toPST = (date) => date ? dayjs(date).tz('America/Los_Angeles') : null;
      const created = toPST(createdAt);
      const updated = toPST(updatedAt);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          
          {/* PAYMENT APPROVED SECTION */}
          <div>
            <Text type="secondary" style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 8 }}>
              <CheckCircleOutlined style={{ marginRight: 4 }} /> Payment Approval
            </Text>
            {!paymentYesDate ? (
              <Tag color="red" icon={<CloseOutlined />} style={{ margin: 0 }}>
                Not Approved
              </Tag>
            ) : (
              <Tooltip title={`Approved on ${formatToPST(paymentYesDate)}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: '#f6ffed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <CheckCircleOutlined style={{ fontSize: 14, color: '#52c41a' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Text strong style={{ fontSize: 13, color: '#52c41a' }}>
                      {dayjs(paymentYesDate).format('MM/DD/YYYY')}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {dayjs(paymentYesDate).format('hh:mm A')}
                    </Text>
                  </div>
                </div>
              </Tooltip>
            )}
          </div>

          <Divider style={{ margin: 0 }} />

          {/* DATE INFO SECTION */}
          <div>
            <Text type="secondary" style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 8 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} /> Timeline
            </Text>
            
            <Tooltip
              title={
                <>
                  <div><strong>Created:</strong> {created ? created.format('MM/DD/YYYY hh:mm A') + ' PST' : 'N/A'}</div>
                  <div><strong>Updated:</strong> {updated ? updated.format('MM/DD/YYYY hh:mm A') + ' PST' : 'N/A'}</div>
                </>
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Created Row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: '#F1EFE8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <ClockCircleOutlined style={{ fontSize: 13, color: '#888780' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
                      {created ? created.format('MM/DD/YY · hh:mm A') : 'N/A'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                      Created · {created ? created.fromNow() : '—'} · PST
                    </Text>
                  </div>
                </div>

                {/* Updated Row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: '#E6F1FB',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <SyncOutlined style={{ fontSize: 13, color: '#185FA5' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2, color: '#185FA5' }}>
                      {updated ? updated.format('MM/DD/YY · hh:mm A') : 'N/A'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                      Updated · {updated ? updated.fromNow() : '—'} · PST
                    </Text>
                  </div>
                </div>
              </div>
            </Tooltip>
          </div>
        </div>
      );
    },
  },
  {
  title: 'Tracking Status',
  key: 'trackingStatus',
  width: 200,
  render: (_, record) => {
    const tracking = record.ShipmentTracking || {};
    const lastUpdated = record.ShipmentTrackingUpdatedAt;
    const lastUpdatedPST = lastUpdated ? dayjs(lastUpdated).tz('America/Los_Angeles') : null;
    
    return (
      <div style={{ 
        background: '#f8f9fa', 
        borderRadius: 12, 
        padding: '12px',
        border: '1px solid #f0f0f0',
        position: 'relative'
      }}>
        {/* Edit Button - Top Right */}
        {isAdmin && (
          <Tooltip title="Edit Pictures Status">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ fontSize: 12 }} />}
              onClick={() => handleTrackingEdit(record)}
              style={{ 
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#1890ff'
              }}
            />
          </Tooltip>
        )}
        
        {/* Pictures Status */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Pictures Status</Text>
            <div style={{ marginTop: 4 }}>
              <Tag 
                color={tracking.picturesStatus === 'Received' ? 'success' : tracking.picturesStatus === 'Pending' ? 'warning' : 'default'}
                icon={tracking.picturesStatus === 'Received' ? <CheckCircleOutlined /> : tracking.picturesStatus === 'Pending' ? <ClockCircleOutlined /> : <CloseCircleOutlined />}
                style={{ fontSize: 12, padding: '4px 12px' }}
              >
                {tracking.picturesStatus || 'Not Received'}
              </Tag>
            </div>
          </div>
        </div>
        
        {/* Pictures Approval */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Pictures Approval</Text>
            <div style={{ marginTop: 4 }}>
              <Tag 
                color={tracking.picturesApproval === 'Yes' ? 'success' : 'error'}
                icon={tracking.picturesApproval === 'Yes' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                style={{ fontSize: 12, padding: '4px 12px' }}
              >
                {tracking.picturesApproval === 'Yes' ? '✓ Approved' : '✗ Not Approved'}
              </Tag>
            </div>
          </div>
        </div>
        
        {/* Last Updated */}
        {lastUpdatedPST && (
          <div style={{ 
            borderTop: '1px dashed #e8e8e8', 
            paddingTop: 8,
            marginTop: 4
          }}>
            <Tooltip title={lastUpdatedPST.format('MMM-DD-YYYY hh:mm A') + ' PST'}>
              <Space size={4}>
                <SyncOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Updated: {lastUpdatedPST.format('MM/DD/YY · hh:mm A')}
                </Text>
              </Space>
            </Tooltip>
          </div>
        )}
      </div>
    );
  },
},
 {
    title: 'Order Accepted',
    key: 'customerAccepted',
    width: 200,
    render: (_, record) => {
        const partStatus = record.orderProcessComplete?.partStatus || 'N/A';
        const isAccepted = partStatus !== 'Customer Canceled';
        // The stored default is "Customer Canceled"; show Pending until someone chooses
        const isPending = isOrderDecisionUnset(record);
        const history = record.orderProcessCompleteHistory || [];

        // Find the last partStatus change from history
        const partStatusChange = [...history]
            .reverse() // Get most recent first
            .find(change => change.field === 'orderProcessComplete.partStatus');

        const changedAt = partStatusChange?.changedAt;

        const statusTag = (
            <Tag
                color={isPending ? 'default' : isAccepted ? 'green' : 'red'}
                icon={isPending ? <ClockCircleOutlined /> : isAccepted ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                style={{
                    margin: 0,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 500,
                    borderRadius: 20,
                    width: '100%',
                    textAlign: 'center'
                }}
            >
                {isPending ? '⏳ Pending' : isAccepted ? '✅ Accepted' : '❌ Canceled'}
            </Tag>
        );

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {isPending ? (
                    <Tooltip title="Not chosen yet. It is set when the shipment details are saved.">
                        {statusTag}
                    </Tooltip>
                ) : statusTag}
                
                {changedAt && (
                    <div style={{ 
                        fontSize: 11, 
                        color: '#666',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                    }}>
                        <CalendarOutlined style={{ fontSize: 10 }} />
                        <span>
                            {new Date(changedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            })}
                        </span>
                        <span style={{ fontSize: 10, color: '#999' }}>
                            {new Date(changedAt).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </span>
                    </div>
                )}
            </div>
        );
    }
},
 {
    title: 'Payment Paid',
    key: 'paymentAllow',
    width: 180,
    render: (_, record) => {
      const paymentAllow = record.payementAllow || 'N/A';
      const updatedAt = record.payementAllowUpdatedAt;
      
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Tag 
            color={paymentAllow === 'Yes' ? 'green' : 'red'}
            icon={paymentAllow === 'Yes' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            style={{ 
              margin: 0, 
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 20,
              width: '100%',
              textAlign: 'center'
            }}
          >
            💰 {paymentAllow === 'Yes' ? 'Allowed' : 'Not Allowed'}
          </Tag>
          
          {updatedAt && (
            <div style={{ 
              fontSize: 10, 
              color: '#8c8c8c',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}>
              <HistoryOutlined style={{ fontSize: 10 }} />
              <span>
                {new Date(updatedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </span>
              <ClockCircleOutlined style={{ fontSize: 10 }} />
              <span>
                {new Date(updatedAt).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          )}
        </div>
      );
    }
  },
{
    title: 'Part Status',
    key: 'partStatus',
    width: 200,
    render: (_, record) => {
      const shipmentPartStatus = record.ShipmentTracking?.partStatus || 'Not Available';
      const history = record.ShipmentTrackingHistory || [];
      
      // Find all partStatus changes
      const partStatusChanges = history
        .filter(change => change.field === 'ShipmentTracking.partStatus')
        .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt));
      
      const latestChange = partStatusChanges[0];
      const previousChange = partStatusChanges[1];
      const changedAt = latestChange?.changedAt;
      
      const getStatusConfig = (status) => {
        switch(status) {
          case 'Available':
            return { color: 'green', icon: <CheckCircleOutlined />, text: 'Available', bg: '#f6ffed' };
          case 'Not Available':
            return { color: 'red', icon: <CloseCircleOutlined />, text: 'Not Available', bg: '#fff2f0' };
          case 'Not Good':
            return { color: 'orange', icon: <ExclamationCircleOutlined />, text: 'Not Good', bg: '#fff7e6' };
          case 'Wrong Part':
            return { color: 'red', icon: <CloseCircleOutlined />, text: 'Wrong Part', bg: '#fff2f0' };
          case 'Customer Canceled Order':
            return { color: 'default', icon: <StopOutlined />, text: 'Canceled', bg: '#fafafa' };
          default:
            return { color: 'default', icon: null, text: status, bg: '#fafafa' };
        }
      };
      
      // The stored default is "Not Available"; show Not Set until someone chooses
      const config = isPartStatusUnset(record)
        ? { color: 'default', icon: null, text: 'Not Set', bg: '#fafafa' }
        : getStatusConfig(shipmentPartStatus);

      return (
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: changedAt ? 8 : 0
          }}>
            <Tag 
              color={config.color}
              icon={config.icon}
              style={{ 
                margin: 0, 
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 500,
                borderRadius: 16,
              }}
            >
              📦 {config.text}
            </Tag>

            {previousChange && (
              <Tooltip title={`Previous status: ${previousChange.newValue}`}>
                <HistoryOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
              </Tooltip>
            )}
          </div>

          {/* Own line: the column is too narrow for the tag and a labelled button */}
          {canCancelOrder && shipmentPartStatus !== 'Customer Canceled Order' && (
            <div style={{ marginTop: 8 }}>
              <Tooltip title="Customer wants to cancel">
                <Button
                  danger
                  size="small"
                  icon={<StopOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCustomerCancel(record);
                  }}
                  style={{ height: 30, padding: '0 10px', borderRadius: 16, fontSize: 12 }}
                >
                  Cancel Order
                </Button>
              </Tooltip>
            </div>
          )}

          {changedAt && (
            <div style={{ 
              fontSize: 11,
              color: '#666',
              backgroundColor: '#fafafa',
              padding: '4px 8px',
              borderRadius: 4,
              marginTop: 4
            }}>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                <span>🕒 {new Date(changedAt).toLocaleDateString()}</span>
                <span>{new Date(changedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>
          )}
        </div>
      );
    }
  }
  ,
 {
    title: 'Miles Promise To Customer',
    key: 'milesPromise',
    width: 280,
    render: (_, record) => {
        const milesPromiseComments = record.milesPromiseCustomer || [];
        const latestComment = milesPromiseComments.length > 0 
            ? milesPromiseComments[milesPromiseComments.length - 1] 
            : null;
        
        return (
            <div>
                {/* Add Comment Button */}
             
                {/* Display Latest Comment */}
                {latestComment ? (
                    <div 
                        style={{
                            background: '#faf5ff',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            border: '1px solid #d8b4fe',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            wordBreak: 'break-word',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLeadForMilesPromise(record);
                            setIsMilesPromiseModalVisible(true);
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px', color: '#6b21a5' }}>
                                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                {formatCommentDate(latestComment.changedAt)}
                            </Text>
                            {milesPromiseComments.length > 1 && (
                                <Tag 
                                    color="purple" 
                                    style={{ 
                                        fontSize: '10px', 
                                        padding: '0 4px', 
                                        margin: 0,
                                        cursor: 'pointer'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedLeadForMilesPromise(record);
                                        setIsMilesPromiseModalVisible(true);
                                    }}
                                >
                                    +{milesPromiseComments.length - 1} more
                                </Tag>
                            )}
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            display: 'block', 
                            color: '#4c1d95',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap'
                        }}>
                            {latestComment.comment.length > 80 
                                ? `${latestComment.comment.substring(0, 80)}...` 
                                : latestComment.comment}
                        </Text>
                    </div>
                ) : (
                    <div 
                        style={{ 
                            textAlign: 'center', 
                            padding: '12px',
                            background: '#faf5ff',
                            borderRadius: '8px',
                            border: '1px solid #d8b4fe',
                            cursor: 'pointer'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLeadForMilesPromise(record);
                            setIsMilesPromiseModalVisible(true);
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: '12px', color: '#6b21a5' }}>
                            <CommentOutlined style={{ marginRight: '4px', fontSize: '14px' }} />
                            No miles promise comments yet
                        </Text>
                    </div>
                )}
                   <Button
                    type="primary"
                    size="small"
                    icon={<CommentOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLeadForMilesPromise(record);
                        setNewMilesPromiseComment('');
                        setIsMilesPromiseModalVisible(true);
                    }}
                    style={{ 
                        marginBottom: '12px',
                        width: '100%',
                        fontSize: '12px',
                        backgroundColor: '#9333ea',
                        borderColor: '#9333ea'
                    }}
                >
                    Add Miles Promise
                </Button>

            </div>
        );
    },
},
{
    title: 'Comment',
    key: 'paymentPageComment',
    width: 280,
    render: (_, record) => {
        const comments = record.paymentPageComment || [];
        const latestComment = comments.length > 0 ? comments[comments.length - 1] : null;
        
        return (
            <div>
             

                {/* Display Latest Comment */}
                {latestComment ? (
                    <div 
                        style={{
                            background: '#fafafa',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            wordBreak: 'break-word',      // Add this
                            wordWrap: 'break-word',       // Add this
                            overflowWrap: 'break-word',   // Add this
                            whiteSpace: 'normal'          // Add this
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLeadForComment(record);
                            setCommentsModalVisible(true);
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                {formatCommentDate(latestComment.changedAt)}
                            </Text>
                            {comments.length > 1 && (
                                <Tag 
                                    color="blue" 
                                    style={{ 
                                        fontSize: '10px', 
                                        padding: '0 4px', 
                                        margin: 0,
                                        cursor: 'pointer'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedLeadForComment(record);
                                        setCommentsModalVisible(true);
                                    }}
                                >
                                    +{comments.length - 1} more
                                </Tag>
                            )}
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            display: 'block', 
                            wordBreak: 'break-word',    // Add this
                            whiteSpace: 'pre-wrap'       // Add this for preserving whitespace and wrapping
                        }}>
                            {latestComment.comment.length > 80 
                                ? `${latestComment.comment.substring(0, 80)}...` 
                                : latestComment.comment}
                        </Text>
                    </div>
                ) : (
                    <div 
                        style={{ 
                            textAlign: 'center', 
                            padding: '12px',
                            background: '#fafafa',
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            cursor: 'pointer'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLeadForComment(record);
                            setCommentsModalVisible(true);
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <CommentOutlined style={{ marginRight: '4px', fontSize: '14px' }} />
                            No comments yet
                        </Text>
                    </div>
                )}
                   <Button
                    type="primary"
                    size="small"
                    icon={<CommentOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLeadForComment(record);
                        setNewComment('');
                        setCommentsModalVisible(true);
                    }}
                    style={{ 
                        marginBottom: '12px',
                        width: '100%',
                        fontSize: '12px'
                    }}
                >
                    Add Comment
                </Button>
            </div>
        );
    },
},
  {
    title: 'Actions',
    key: 'actions',
    width: 90,
    fixed: 'right',
    align: 'center',
    render: (_, record) => (
      <Tooltip title="View Lead Details">
        <Button 
          type="primary"
          shape="circle"
          icon={<EyeOutlined />} 
          onClick={() => handleView(record)}
          style={{ 
            background: `linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)`,
            border: 'none',
            boxShadow: '0 2px 8px rgba(24,144,255,0.3)',
            width: 32,
            height: 32
          }}
        />
      </Tooltip>
    ),
  },
];

  const visibleColumns = columns
    .filter((column) => column.key !== 'trackingStatus')
    .map((column) => {
      if (column.key === 'amount') {
        return {
          ...column,
          width: 140,
          render: (_, record) => {
            const totalAmount = record.totalAmount || 0;

            return (
              <Card
                size="small"
                style={{ borderRadius: 10, background: '#fafafa', border: 'none' }}
                bodyStyle={{ padding: '7px 10px' }}
              >
                <Space size={4}>
                  <DollarOutlined style={{ color: '#1890ff', fontSize: 11 }} />
                  <Text style={{ fontSize: 11 }}>Total:</Text>
                  <Tag
                    color={totalAmount > 1000 ? 'volcano' : totalAmount > 500 ? 'orange' : 'green'}
                    style={{ borderRadius: 16, padding: '2px 8px', fontWeight: 600, margin: 0 }}
                  >
                    {formatCurrency(totalAmount)}
                  </Tag>
                </Space>
              </Card>
            );
          },
        };
      }

      if (column.key === 'documentStatus') {
        return {
          ...column,
          width: 156,
          render: (_, record) => (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                minHeight: 32,
              }}
            >
             
              <div style={{ flex: 1, minWidth: 0 }}>
                  {renderEditableCell(record, 'purchaseOrderStatus', purchaseOrderStatusOptions, 'po', 'purchaseOrderStatusHistory')}
              </div>
            </div>
          ),
        };
      }

      if (column.key === 'datePayment') {
        return {
          ...column,
          width: 190,
          sorter: (a, b) => {
            const dateA = getLastPaymentDate(a);
            const dateB = getLastPaymentDate(b);

            if (dateA && dateB) return new Date(dateB) - new Date(dateA);
            if (dateA) return -1;
            if (dateB) return 1;
            return 0;
          },
          render: (_, record) => {
            const lastPaymentDate = getLastPaymentDate(record);
            const lastPaymentDatePST = lastPaymentDate ? dayjs(lastPaymentDate).tz('America/Los_Angeles') : null;

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 500, display: 'block' }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} /> Last Payment Date
                </Text>

                <Tooltip title={lastPaymentDatePST ? `${lastPaymentDatePST.format('MM/DD/YYYY hh:mm A')} PST` : 'N/A'}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 24,
                      height: 24,
                      borderRadius: 8,
                      background: '#EAF7EE',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <ClockCircleOutlined style={{ fontSize: 12, color: '#237804' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2, color: '#237804' }}>
                        {lastPaymentDatePST ? lastPaymentDatePST.format('MM/DD/YY hh:mm A') : 'N/A'}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 10, lineHeight: 1.2 }}>
                        {lastPaymentDatePST ? 'PST' : 'Last Payment'}
                      </Text>
                    </div>
                  </div>
                </Tooltip>
              </div>
            );
          },
        };
      }

      return column;
    });

  return (
    <div style={{ 
      padding: 24, 
      minHeight: "100vh", 
      background: `linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)`
    }}>
      {/* Modern Header with Stats */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card 
          style={{ 
            marginBottom: 24, 
            borderRadius: 24,
            border: 'none'
          }}
          bodyStyle={{ padding: '32px' }}
        >
          <Row gutter={[24, 24]} align="middle">
            <Col xs={24} md={12}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <Avatar 
                  size={72} 
                  style={{ 
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    color:"black"
                  }}
                  icon={<ShoppingCartOutlined style={{ fontSize: 36 }} />}
                />
                <div>
                  <Title level={2} style={{ margin: 0, fontWeight: 700 }}>
                    Order Process
                  </Title>
                  <Text style={{ fontSize: 14 }}>
                    Manage and track order processing status
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      </motion.div>

      {/* Filter Section */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card 
          bordered={false}
          style={{ 
            borderRadius: 20,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            marginBottom: 24,
            border: '1px solid #f0f0f0'
          }}
          bodyStyle={{ padding: '20px 24px' }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8}>
              <Space.Compact block>
                <SearchFieldsSelect value={searchFields} onChange={setSearchFields} />
                <Input
                  placeholder={getSearchFieldsPlaceholder(searchFields)}
                  prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  allowClear
                  size="large"
                />
              </Space.Compact>
            </Col>
            {isAdmin && (
              <Col xs={24} md={4}>
                <Select
                  placeholder="Filter by Agent"
                  value={selectedAgentId}
                  onChange={setSelectedAgentId}
                  style={{ width: '100%', borderRadius: 12 }}
                  size="large"
                  allowClear
                  suffixIcon={<TeamOutlined />}
                  showSearch
                  optionFilterProp="children"
                >
                  {agents.map(agent => (
                    <Option key={agent._id} value={agent._id}>
                      <Space>
                        <Avatar size="small" icon={<UserOutlined />} />
                        <span>{agent.firstName} {agent.lastName}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            <Col xs={24} md={4}>
              <Select
                placeholder="Quick Date"
                style={{ width: '100%', borderRadius: 12 }}
                onChange={handleQuickDateFilter}
                value={quickDateFilter}
                size="large"
              >
                <Option value="today">Today</Option>
                <Option value="yesterday">Yesterday</Option>
                <Option value="thisWeek">This Week</Option>
                <Option value="thisMonth">This Month</Option>
                <Option value="lastMonth">Last Month</Option>
                <Option value="thisQuarter">This Quarter</Option>
                <Option value="thisYear">This Year</Option>
                <Option value="custom">Custom Range</Option>
              </Select>
            </Col>
            <Col xs={24} md={isAdmin ? 7 : 8}>
              <RangePicker
                style={{ width: '100%', borderRadius: 12 }}
                onChange={handleDateRangeChange}
                value={dateRange}
                size="large"
                format="MM/DD/YYYY"
                placeholder={['Start Date', 'End Date']}
              />
            </Col>
            <Col xs={24} md={isAdmin ? 4 : 6}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Tooltip title="Refresh">
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={handleRefresh}
                    loading={refreshing}
                    size="large"
                    style={{ borderRadius: 12 }}
                  />
                </Tooltip>
                <Tooltip title="Clear Filters">
                  <Button 
                    icon={<ClearOutlined />} 
                    onClick={handleClearFilters}
                    size="large"
                    style={{ borderRadius: 12 }}
                  />
                </Tooltip>
              </Space>
            </Col>
          </Row>

         {/* Date Range Info */}
<div style={{ 
    marginTop: 16,
    backgroundColor: '#f0f5ff', 
    padding: '8px 12px', 
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8
}}>
    <CalendarOutlined style={{ color: '#1890ff' }} />
    <Text type="secondary">
        Showing orders from: <Text strong>
            {dateRange?.[0]?.isValid() ? dateRange[0].format('MM/DD/YYYY') : dayjs().startOf('day').format('MM/DD/YYYY')}
        </Text> to <Text strong>
            {dateRange?.[1]?.isValid() ? dateRange[1].format('MM/DD/YYYY') : dayjs().endOf('day').format('MM/DD/YYYY')}
        </Text>
    </Text>
    <Badge 
        count={totalRecords} 
        showZero 
        color="#52c41a" 
        style={{ marginLeft: 'auto' }}
    />
</div>
        </Card>
      </motion.div>

      {/* Results Header */}
      <div style={{
        padding: '16px 20px',
        background: '#ffffff',
        borderTop: '1px solid #f0f0f0',
        borderBottom: '1px solid #f0f0f0',
        borderRadius: '12px 12px 0 0',
        marginTop: 8
      }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size={16}>
             <Text strong style={{ fontSize: 16 }}>
    {isSearching ? (
        <>
            <SearchOutlined style={{ color: '#1890ff', marginRight: 8 }} />
            Search Results
        </>
    ) : (
        <>
            <CalendarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            {dateRange && dateRange[0] && dateRange[1] && dateRange[0].isValid() && dateRange[1].isValid() ? (
                dateRange[0].isSame(dateRange[1], 'day') ? (
                    dateRange[0].format('MM/DD/YYYY')
                ) : (
                    `${dateRange[0].format('MMM D')} → ${dateRange[1].format('MM/DD/YYYY')}`
                )
            ) : (
                `${dayjs().startOf('day').format('MMM D')} → ${dayjs().endOf('day').format('MM/DD/YYYY')}`
            )}
        </>
    )}
</Text>
              <Badge
                count={leads.length}
                showZero
                style={{
                  backgroundColor: '#52c41a',
                  fontSize: 12,
                  fontWeight: 'bold'
                }}
              />
              <Text type="secondary">
                of {totalRecords.toLocaleString()} total records
              </Text>
            </Space>
          </Col>
          <Col>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              showSizeChanger
              pageSizeOptions={["10", "20", "50", "100"]}
              onChange={handlePaginationChange}
              size="small"
              simple
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
            />
          </Col>
        </Row>
      </div>

      {/* Main Table */}
      <Card 
        bordered={false}
        style={{ 
          borderRadius: '0 0 20px 20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          borderTop: 'none'
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={visibleColumns}
          dataSource={leads}
          rowKey="_id"
          loading={loading}
          pagination={false}
          onChange={handleTableChange}
          scroll={{ x: isAdmin ? 1990 : 1830, y: 'calc(100vh - 550px)' }}
          bordered={false}
          size="middle"
          rowClassName={() => 'table-row-hover'}
          style={{
            background: 'transparent'
          }}
          expandable={{
            expandedRowRender: expandedRowRender,
            expandedRowKeys: expandedRowKeys,
            onExpand: handleExpand,
            expandIcon: ({ expanded, onExpand, record }) => (
              <Tooltip title={expanded ? "Hide Yard Details" : "Show Yard Details"}>
                <Button
                  type="text"
                  icon={expanded ? <CloseOutlined /> : <PlusOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onExpand(record, e);
                  }}
                  style={{
                    marginRight: 8,
                    color: '#722ed1',
                    fontSize: 16,
                    width: 28,
                    height: 28,
                    borderRadius: 14,
                    background: expanded ? '#f0f0ff' : '#f5f5f5'
                  }}
                />
              </Tooltip>
            ),
            expandRowByClick: false,
          }}
        />

        {/* Bottom Pagination */}
        {leads.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 32,
            padding: '20px 24px',
            background: '#fafafa',
            borderRadius: 12,
            margin: 24
          }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              showSizeChanger
              pageSizeOptions={["10", "20", "50", "100"]}
              onChange={handlePaginationChange}
              showQuickJumper
              showTotal={(total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} orders`
              }
              size="default"
            />
          </div>
        )}
      </Card>

      {/* Amount Edit Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <DollarOutlined style={{ fontSize: 20, color: 'white' }} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0 }}>Edit Total Amount</Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Update total amount for this lead
              </Text>
            </div>
          </div>
        }
        open={isAmountModalVisible}
        onCancel={handleAmountModalCancel}
        footer={null}
        width={480}
        centered
        modalRender={(modal) => (
          <div style={{ borderRadius: 20, overflow: 'hidden' }}>
            {modal}
          </div>
        )}
      >
        <Form
          form={amountForm}
          layout="vertical"
          onFinish={handleAmountModalSave}
          style={{ marginTop: 16 }}
        >
          {selectedLeadForAmount && (
            <>
              {/* Customer Info Card */}
              <Card 
                size="small" 
                style={{ 
                  marginBottom: 20,
                  background: '#f5f5f5',
                  borderRadius: 12,
                  border: 'none'
                }}
                bodyStyle={{ padding: '12px 16px' }}
              >
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text strong>
                    {selectedLeadForAmount.personalInfo?.fullName || 'N/A'}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {selectedLeadForAmount.vehicleInfo?.make} {selectedLeadForAmount.vehicleInfo?.model} - {selectedLeadForAmount.vehicleInfo?.part}
                  </Text>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8,
                    marginTop: 4,
                    paddingTop: 4,
                    borderTop: '1px solid #e8e8e8'
                  }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Current Amount: 
                    </Text>
                    <Text strong style={{ fontSize: 12, color: '#1890ff' }}>
                      {formatCurrency(selectedLeadForAmount.totalAmount || 0)}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Paid Amount: 
                    </Text>
                    <Text strong style={{ fontSize: 12, color: '#52c41a' }}>
                      {formatCurrency(selectedLeadForAmount.paidAmount || 0)}
                    </Text>
                  </div>
                </Space>
              </Card>

              {/* Total Amount Input */}
              <Form.Item
                name="totalAmount"
                label="New Total Amount"
                rules={[
                  { required: true, message: 'Please enter total amount' },
                  { 
                    validator: (_, value) => {
                      if (value && value <= 0) {
                        return Promise.reject('Amount must be greater than 0');
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  size="large"
                  min={0}
                  step={0.01}
                  prefix="$"
                  placeholder="Enter new total amount"
                  formatter={(value) => 
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  parser={(value) => 
                    value.replace(/\$\s?|(,*)/g, '')
                  }
                  autoFocus
                />
              </Form.Item>

              {/* Info about balance calculation */}
              {(selectedLeadForAmount.paidAmount || 0) > 0 && (
                <div style={{ 
                  background: '#fff7e6', 
                  padding: '12px', 
                  borderRadius: 8,
                  marginBottom: 20,
                  border: '1px solid #ffe7ba'
                }}>
                  <Space size={8}>
                    <WalletOutlined style={{ color: '#faad14' }} />
                    <Text style={{ fontSize: 12 }}>
                      Current balance will be recalculated based on paid amount.
                    </Text>
                  </Space>
                </div>
              )}

              {/* Modal Actions */}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                <Button onClick={handleAmountModalCancel} size="large">
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={savingAmount}
                  size="large"
                  icon={<CheckOutlined />}
                >
                  Update Amount
                </Button>
              </div>
            </>
          )}
        </Form>
      </Modal>

      
{/* Tracking Status Edit Modal */}
<Modal
  title={
    <Space>
      <EditOutlined style={{ color: '#1890ff' }} />
      <Text strong style={{ fontSize: 18 }}>Edit Pictures Status</Text>
    </Space>
  }
  open={trackingModalVisible}
  onCancel={() => {
    setTrackingModalVisible(false);
    trackingForm.resetFields();
    setSelectedTrackingRecord(null);
  }}
  footer={[
    <Button 
      key="cancel" 
      onClick={() => {
        setTrackingModalVisible(false);
        trackingForm.resetFields();
        setSelectedTrackingRecord(null);
      }} 
      icon={<CloseOutlined />} 
      disabled={updatingTracking}
    >
      Cancel
    </Button>,
    <Button 
      key="save" 
      type="primary" 
      onClick={handleTrackingSave}
      icon={<SaveOutlined />}
      loading={updatingTracking}
    >
      Save Changes
    </Button>
  ]}
  width={480}
  centered
>
  <Form form={trackingForm} layout="vertical">
    <Form.Item 
      name="picturesStatus" 
      label="Pictures Status" 
      rules={[{ required: true, message: 'Please select pictures status' }]}
    >
      <Select placeholder="Select pictures status">
        <Option value="Received">
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <span>Received</span>
          </Space>
        </Option>
        <Option value="Not Received">
          <Space>
            <CloseCircleOutlined style={{ color: '#f5222d' }} />
            <span>Not Received</span>
          </Space>
        </Option>
        <Option value="Pending">
          <Space>
            <ClockCircleOutlined style={{ color: '#faad14' }} />
            <span>Pending</span>
          </Space>
        </Option>
      </Select>
    </Form.Item>
    
    <Form.Item 
      name="picturesApproval" 
      label="Pictures Approval" 
      rules={[{ required: true, message: 'Please select pictures approval' }]}
    >
      <Select placeholder="Select approval status">
        <Option value="Yes">
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <span>Yes</span>
          </Space>
        </Option>
        <Option value="No">
          <Space>
            <CloseCircleOutlined style={{ color: '#f5222d' }} />
            <span>No</span>
          </Space>
        </Option>
      </Select>
    </Form.Item>
  </Form>
</Modal>
      <style>{`
        .table-row-hover:hover {
          background: linear-gradient(90deg, #f0f7ff 0%, #ffffff 100%) !important;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f0f0f0 !important;
        }
        
        .ant-table-thead > tr > th {
          background: #fafafa !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          color: #1f2937 !important;
          border-bottom: 2px solid #f0f0f0 !important;
        }
        
        /* Fix for edit button visibility */
        .ant-table-cell {
          position: relative;
        }
        
        .ant-btn-text {
          opacity: 0.7;
          transition: opacity 0.3s;
        }
        
        .ant-btn-text:hover {
          opacity: 1;
          background: rgba(24, 144, 255, 0.1) !important;
        }
        
        /* Ensure edit button is always visible */
        .ant-tag + .ant-btn-text {
          margin-left: 8px;
        }
        
        .ant-modal-content {
          border-radius: 20px !important;
        }
        
        .ant-modal-header {
          border-radius: 20px 20px 0 0 !important;
          border-bottom: none !important;
          padding: 24px 24px 0 24px !important;
        }
        
        .ant-modal-body {
          padding: 24px !important;
        }
      `}</style>
          <Modal
         
          open={commentsModalVisible}
          onCancel={() => {
              setCommentsModalVisible(false);
              setSelectedLeadForComment(null);
              setNewComment('');
          }}
          footer={[
              <Button key="cancel" onClick={() => {
                  setCommentsModalVisible(false);
                  setSelectedLeadForComment(null);
                  setNewComment('');
              }}>
                  Cancel
              </Button>,
              <Button 
                  key="submit" 
                  type="primary" 
                  onClick={handleAddComment}
                  loading={addingComment}
                  disabled={!newComment.trim()}
              >
                  Add Comment
              </Button>
          ]}
          width={500}
      >
          <div style={{ padding: '16px 0' }}>
            
      
              <div>
                  <Text strong>Comment:</Text>
                  <Input.TextArea
                      placeholder="Enter your comment here..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={4}
                      maxLength={1000}
                      showCount
                      style={{ marginTop: '8px' }}
                      autoFocus
                  />
              </div>
      
              {/* Show existing comments if any */}
              {selectedLeadForComment?.paymentPageComment?.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                      <Divider />
                      <Text strong>Previous Comments ({selectedLeadForComment.paymentPageComment.length})</Text>
                      <div style={{ 
                          maxHeight: '200px', 
                          overflowY: 'auto', 
                          marginTop: '8px',
                          padding: '8px',
                          background: '#f5f5f5',
                          borderRadius: '8px'
                      }}>
                          {[...selectedLeadForComment.paymentPageComment]
                              .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                              .map((comment, idx) => (
                                  <div key={idx} style={{ 
                                      marginBottom: '12px', 
                                      padding: '8px',
                                      background: 'white',
                                      borderRadius: '6px',
                                      border: '1px solid #e8e8e8'
                                  }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                          <Text type="secondary" style={{ fontSize: '11px' }}>
                                              <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                              {formatCommentDate(comment.changedAt)}
                                          </Text>
                                      </div>
                                      <Text style={{ fontSize: '13px' }}>{comment.comment}</Text>
                                  </div>
                              ))}
                      </div>
                  </div>
              )}
          </div>
      </Modal>
      {/* Service Agent Assignment Modal */}
<Modal
    title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <TeamOutlined style={{ fontSize: 20, color: 'white' }} />
            </div>
            <div>
                <Title level={4} style={{ margin: 0 }}>Assign Service Agent</Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Select service agent for this lead
                </Text>
            </div>
        </div>
    }
    open={serviceAgentModalVisible}
    onCancel={() => {
        setServiceAgentModalVisible(false);
        setSelectedLeadForServiceAgent(null);
        setSelectedServiceAgentForLead(null);
    }}
    footer={null}
    width={500}
    centered
>
    <div style={{ padding: '16px 0' }}>
        {selectedLeadForServiceAgent && (
            <>
                {/* Customer Info Card */}
                <Card 
                    size="small" 
                    style={{ 
                        marginBottom: 20,
                        background: '#f5f5f5',
                        borderRadius: 12,
                        border: 'none'
                    }}
                    bodyStyle={{ padding: '12px 16px' }}
                >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Text strong>
                            {selectedLeadForServiceAgent.personalInfo?.fullName || 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {selectedLeadForServiceAgent.vehicleInfo?.make} {selectedLeadForServiceAgent.vehicleInfo?.model} - {selectedLeadForServiceAgent.vehicleInfo?.part}
                        </Text>
                        {selectedLeadForServiceAgent.orderServiceHandledBy && (
                            <div style={{ 
                                marginTop: 8,
                                paddingTop: 8,
                                borderTop: '1px solid #e8e8e8'
                            }}>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Currently assigned: 
                                </Text>
                                <Text strong style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }}>
                                    {getServiceAgentName(selectedLeadForServiceAgent)}
                                </Text>
                            </div>
                        )}
                    </Space>
                </Card>

                {/* Service Agent Selection */}
                <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Select Service Agent:
                    </Text>
                    <Select
                        value={selectedServiceAgentForLead}
                        onChange={setSelectedServiceAgentForLead}
                        style={{ width: '100%' }}
                        size="large"
                        placeholder="Choose an agent"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        loading={loadingServiceAgents}
                    >
                        {Object.entries(serviceAgentsMap).map(([agentId, agentInfo]) => (
                            <Option key={agentId} value={agentId}>
                                <Space direction="vertical" size={0}>
                                    <Text strong>{agentInfo.name}</Text>
                                 
                                </Space>
                            </Option>
                        ))}
                    </Select>
                    <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                        {selectedServiceAgentForLead ? 'Agent will be assigned to this lead' : 'Clear to unassign the current agent'}
                    </Text>
                </div>

                {/* Modal Actions */}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <Button 
                        onClick={() => {
                            setServiceAgentModalVisible(false);
                            setSelectedLeadForServiceAgent(null);
                            setSelectedServiceAgentForLead(null);
                        }} 
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleServiceAgentSave}
                        loading={savingServiceAgent}
                        size="large"
                        icon={<CheckOutlined />}
                    >
                        {selectedServiceAgentForLead ? 'Assign Agent' : 'Unassign Agent'}
                    </Button>
                </div>
            </>
        )}
    </div>
</Modal>
{selectedLeadForYard && (
    <YardModal
        visible={yardModalVisible}
        onCancel={handleYardModalClose}
        onOk={handleYardModalSuccess}
        lead={selectedLeadForYard}
    />
)}

<YardReassignmentHistoryModal
    open={!!yardHistoryLead}
    onClose={() => setYardHistoryLead(null)}
    history={yardHistoryLead?.yardReassignmentHistory}
    leadId={yardHistoryLead?._id}
    yards={yardHistoryLead?.yard}
    yardDetailsMap={yardDetailsMap}
    leadName={yardHistoryLead?.personalInfo?.fullName}
    orderNumber={yardHistoryLead?.orderNumber}
    onNotesChanged={() => fetchLeads(true)}
/>


{/* Purchase Order Modal */}
<Modal
    title={
        <Space>
            <ShoppingCartOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontSize: '18px', fontWeight: 600 }}>Purchase Order</span>
        </Space>
    }
    open={poModalVisible}
    onCancel={handlePOModalClose}
    footer={null}
    width={1200}
    centered
    style={{ top: 20 }}
    destroyOnClose
>
    {selectedLeadForPO && (
        <PurchaseOrderTemplate
            lead={selectedLeadForPO}
            onSendSuccess={handlePOSendSuccess}
        />
    )}
</Modal>

{/* Document Status Change History Modal */}
<Modal
    title={
        <Space>
            <HistoryOutlined />
            <span>{statusHistoryModalTitle} — Change History</span>
        </Space>
    }
    open={statusHistoryModalVisible}
    onCancel={() => setStatusHistoryModalVisible(false)}
    footer={null}
    width={560}
>
    <Table
        dataSource={statusHistoryModalEntries.map((entry, index) => ({ ...entry, key: index }))}
        pagination={false}
        size="small"
        locale={{ emptyText: 'No history available' }}
        columns={[
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status) => <Tag>{(status || '').replace(/-/g, ' ')}</Tag>
            },
            {
                title: 'Changed By',
                dataIndex: 'changedBy',
                key: 'changedBy',
                render: (changedBy) => {
                    const name = (() => {
                        if (!changedBy || typeof changedBy !== 'object') return null;
                        return (
                            changedBy.fullName ||
                            changedBy.name ||
                            `${changedBy.firstName || ''} ${changedBy.lastName || ''}`.trim() ||
                            changedBy.userName ||
                            null
                        );
                    })();
                    return name
                        ? <Space size={4}><UserOutlined style={{ fontSize: 11 }} />{name}</Space>
                        : <Text type="secondary">Unknown</Text>;
                }
            },
            {
                title: 'Changed At',
                dataIndex: 'changedAt',
                key: 'changedAt',
                render: (changedAt) => changedAt ? dayjs(changedAt).format('MM/DD/YY hh:mm A') : '—'
            }
        ]}
    />
</Modal>

{/* Miles Promise Customer Modal */}
<Modal
    title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #9333ea 0%, #a855f7 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <CommentOutlined style={{ fontSize: 20, color: 'white' }} />
            </div>
            <div>
                <Title level={4} style={{ margin: 0 }}>
                    Miles Promise Customer Agreement
                </Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {selectedLeadForMilesPromise?.personalInfo?.fullName || 'Customer'}
                </Text>
            </div>
        </div>
    }
    open={isMilesPromiseModalVisible}
    onCancel={() => {
        setIsMilesPromiseModalVisible(false);
        setSelectedLeadForMilesPromise(null);
        setNewMilesPromiseComment('');
    }}
    footer={[
        <Button 
            key="cancel" 
            onClick={() => {
                setIsMilesPromiseModalVisible(false);
                setSelectedLeadForMilesPromise(null);
                setNewMilesPromiseComment('');
            }}
        >
            Close
        </Button>,
        <Button 
            key="submit" 
            type="primary" 
            onClick={handleAddMilesPromiseComment}
            loading={addingMilesPromiseComment}
            disabled={!newMilesPromiseComment.trim()}
            style={{
                backgroundColor: '#9333ea',
                borderColor: '#9333ea'
            }}
        >
            Add Comment
        </Button>
    ]}
    width={600}
    centered
>
    <div style={{ padding: '16px 0' }}>
        {/* Vehicle Info Summary */}
        {selectedLeadForMilesPromise && (
            <Card 
                size="small" 
                style={{ 
                    marginBottom: 20,
                    background: '#faf5ff',
                    borderRadius: 12,
                    border: '1px solid #d8b4fe'
                }}
                bodyStyle={{ padding: '12px 16px' }}
            >
                <Row gutter={[16, 8]}>
                    <Col span={12}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Customer</Text>
                        <div>
                            <Text strong>{selectedLeadForMilesPromise.personalInfo?.fullName || 'N/A'}</Text>
                        </div>
                    </Col>
                    <Col span={12}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Vehicle</Text>
                        <div>
                            <Text strong>
                                {selectedLeadForMilesPromise.vehicleInfo?.year || ''} {selectedLeadForMilesPromise.vehicleInfo?.make || ''} {selectedLeadForMilesPromise.vehicleInfo?.model || ''}
                            </Text>
                        </div>
                    </Col>
                    <Col span={12}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Part</Text>
                        <div>
                            <Tag color="purple">{selectedLeadForMilesPromise.vehicleInfo?.part || 'N/A'}</Tag>
                        </div>
                    </Col>
                    <Col span={12}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Order Number</Text>
                        <div>
                            <Text strong code>{selectedLeadForMilesPromise.orderNumber || 'N/A'}</Text>
                        </div>
                    </Col>
                </Row>
            </Card>
        )}
        
        {/* Add New Comment */}
        <div style={{ marginBottom: 24 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Add New Comment:
            </Text>
            <Input.TextArea
                placeholder="Enter miles promise agreement details with customer..."
                value={newMilesPromiseComment}
                onChange={(e) => setNewMilesPromiseComment(e.target.value)}
                rows={4}
                maxLength={1000}
                showCount
                autoFocus
            />
        </div>
        
        {/* Show existing comments history */}
        {selectedLeadForMilesPromise?.milesPromiseCustomer?.length > 0 && (
            <div>
                <Divider />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <HistoryOutlined style={{ color: '#9333ea' }} />
                    <Text strong>Comment History ({selectedLeadForMilesPromise.milesPromiseCustomer.length})</Text>
                </div>
                <div style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto', 
                    padding: '8px',
                    background: '#faf5ff',
                    borderRadius: '12px'
                }}>
                    {[...selectedLeadForMilesPromise.milesPromiseCustomer]
                        .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                        .map((comment, idx) => (
                            <div key={idx} style={{ 
                                marginBottom: '12px', 
                                padding: '12px',
                                background: 'white',
                                borderRadius: '8px',
                                border: '1px solid #d8b4fe'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <Space size={4}>
                                        <ClockCircleOutlined style={{ color: '#9333ea', fontSize: 11 }} />
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            {dayjs(comment.changedAt).format('MMM DD, YYYY hh:mm A')}
                                        </Text>
                                    </Space>
                                    {idx === 0 && (
                                        <Tag color="purple" style={{ fontSize: 10 }}>
                                            Latest
                                        </Tag>
                                    )}
                                </div>
                                <Text style={{ 
                                    fontSize: 13,
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {comment.comment}
                                </Text>
                            </div>
                        ))}
                </div>
            </div>
        )}
    </div>
</Modal>
    </div>
  );
};

export default OrderProcess;
